import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { CONTRACT_ANALYSIS_URL, CONTRACT_COMPLETE_URL, SCHEDULE_ANALYSIS_URL, CORRESPONDENCE_API_URL } from "../config/api";
import { sqlReadRaw as sqlRead, turnoverWrite as sqlWrite, payloadOf } from "../utils/sqlClient";
import { authHeader } from "../utils/apiToken";
import { callClaude } from "../utils/claudeClient";
import { loginScopes } from "../auth/msalConfig";
import { getMailToken } from "../utils/msalHelpers";
import { useIsMobile } from "../hooks/useIsMobile";
import { usePolledJob } from "../hooks/usePolledJob";
import { syncHubSpotData } from "../utils/hubspotSync";
import { openTurnoverPackagePrint, buildTurnoverPackageHtml } from "../utils/turnoverPrint";
import { contractMarkdownToEmailHtml } from "../utils/emailMarkdown";
import { parseLocalDate, fmtDate, fmtDateTime, fmtCurrency, getYear, bySummaryNum, NAVY_ACTION, CORRESPONDENCE_PROMPT_CATEGORY } from "./turnover/shared";
import { getGraphToken, getEstimatingDrive, uploadToEstimatingTurnover, getConstructionDrive, uploadToSharePoint, entityBasePath, listFolderFilesDeep } from "../utils/sharePoint";
import AISummaryFlyout from "./turnover/AISummaryFlyout";
import CorrespondenceTimelineFlyout from "./turnover/CorrespondenceTimelineFlyout";
import ContractReviewPanel from "./turnover/ContractReviewPanel";
import MultiDocPanel from "./turnover/MultiDocPanel";
import ActionItemRow from "./turnover/ActionItemRow";
import CoverSheetPanel from "./turnover/CoverSheetPanel";
import LongLeadItemsPanel from "./turnover/LongLeadItemsPanel";
import RaciMatrixPanel, { isCsScope, RACI_PHASES } from "./turnover/RaciMatrixPanel";
import NextActionBanner from "./turnover/NextActionBanner";
import ScheduleMilestonesPanel from "./turnover/ScheduleMilestonesPanel";
import PermitsTermsPanel from "./turnover/PermitsTermsPanel";
import FinancialCompliancePanel from "./turnover/FinancialCompliancePanel";
import SectionSidebar from "./turnover/SectionSidebar";
import ReleaseReconciliation from "./turnover/ReleaseReconciliation";
import { checkEstimateReleases, estimateDocGroups, ESTIMATE_DOC_TYPE } from "./turnover/releaseCheck";
import { buildReviewInstructionsHtml } from "./turnover/contractReviewEmail";
import { assignJobNumberUrl } from "./turnover/assignLink";
import ContractIssuesPanel from "./turnover/ContractIssuesPanel";
import ContractRequirementsPanel from "./turnover/ContractRequirementsPanel";
import MarketingKickoffPanel from "./turnover/MarketingKickoffPanel";
import { turnoverFolderLocation, jobFolderName, setFolderPayload } from "./turnover/folderPath";
import ChangeFolderDialog from "./turnover/ChangeFolderDialog";
import { copyDocsToJobFolder, docsInEstimatingSite, copySummary } from "./turnover/moveDocsToJobFolder";
import { buildFileIndex, findDuplicate, addToIndex, findJobFolderId, locateJobFolder, uploadDuplicatePrompt, duplicateLocation } from "./turnover/duplicateFiles";
import { quickXorHashOfFile } from "../utils/quickXorHash";
import { hasReviewChecklist, reviewItemPayload } from "../config/contractReviewItems";
import { withExtraDocTypes } from "../config/turnoverOptions";
import { sectionKind, sectionNumberOf, lastSectionNumber, hasMarketingKickoff, subfolderNames, layoutOf, docTypesFor, isConditionalSection, sharesWithSalesperson } from "../config/turnoverLayout";
import { evaluateRequirements, requirementPayload } from "../config/contractRequirements";
import ContractExecutionPanel from "./turnover/ContractExecutionPanel";
import { commissionIssue, noCommissionSplits } from "./turnover/commission";
import { DIRECTORY_SELECT, toDirectoryUsers } from "./turnover/directoryUsers";
import {
  contractGate, canResolveIssues, canActForReview,
  createIssuePayload, deleteIssuePayload, resolveIssuePayload, acceptIssuePayload, reopenIssuePayload,
  markExecutedPayload, clearExecutedPayload, outcomeRecordedEmail, issueReopenedEmail, readyToSignEmail,
  jobNumberExceptionPayload, exceptionOutstanding, exceptionNoteHtml,
} from "./turnover/contractIssues";
import { buildPmAssignmentEmail, pmAlertRecipients } from "./turnover/pmAssignmentEmail";
import { canEditKickoff, kickoffMissing, normalizeKickoff } from "../config/marketingKickoff";
import { FINANCIAL_COMPLIANCE_FIELDS, TURNOVER_ONLY_FIELDS } from "../config/financialCompliance";
import { StatusPill, DropZone, KV, AssigneeControl } from "./turnover/SectionWidgets";

// ── Constants ──────────────────────────────────────────────────────────────────
// Graph + the Estimating SharePoint folder root (Construction-site folder
// provisioning lives in Turnovers.jsx; its constants were removed here 2026-09-18).
const GRAPH_BASE       = "https://graph.microsoft.com/v1.0";
const EST_BASE_PATH    = "General";

// Standing recipients for the "submit for job number" notification to Accounting
const JOB_NUMBER_NOTIFY_TO = { name: "Nicole Markley", email: "nmarkley@cleanspaceus.com" };
const JOB_NUMBER_NOTIFY_CC = [
  { name: "Emily Rogers",   email: "erogers@cleanspaceus.com" },
  { name: "Ryan Duritsky",  email: "rduritsky@cleanspaceus.com" },
  { name: "Nick Vermont",   email: "nvermont@cleanspaceus.com" },
];

// Ordered lifecycle stages for the segmented progress bar — index also drives
// which stages render as complete / current / upcoming.
const TURNOVER_STAGES = [
  "Contract Review",
  "Pending Number",
  "Numbered",
  "Active",
  "In Progress",
  "Package Complete",
  "Turned Over",
];


// Extracts the actual section number from a "## N. Title" heading so saved rows
// are numbered by what Claude wrote, not by their position in the split array
// (Claude doesn't always emit headers in ascending order, e.g. risk-flag items
// numbered within "## 4" can appear before "## 1" Contract Summary).
function sectionNumFromHeader(text, fallbackIndex) {
  const match = (text || "").match(/^##\s+(\d+)/);
  return match ? parseInt(match[1], 10) : fallbackIndex;
}

// ── Shared AI summary save helper ──────────────────────────────────────────────
async function saveAISummaryParts(turnoverID, sectionID, typePrefix, text, documentsUsed, generatedByRaw) {
  // Standard T-SQL escaping ('' doubling) on the final JSON string — do NOT
  // strip apostrophes; that silently corrupts summary prose ("client's" → "clients").
  const esc = (s) => s.replace(/'/g, "''");
  const generatedBy = generatedByRaw || "";
  const clearPayload = JSON.stringify({ turnover_id: parseInt(turnoverID), section_id: sectionID, type_prefix: typePrefix });
  await sqlWrite(`EXEC sp_ClearTurnoverAISummaries @payload = '${esc(clearPayload)}'`);
  const parts = text.split(/(?=##\s+\d)/);
  const parsed = parts.filter(s => s.trim().length > 10);
  for (let i = 0; i < parsed.length; i++) {
    const secText = parsed[i].substring(0, 3900);
    if (!secText.trim()) continue;
    const secNum = sectionNumFromHeader(parsed[i], i + 1);
    const payload = JSON.stringify({
      turnover_id:    parseInt(turnoverID),
      section_id:     sectionID,
      summary_type:   typePrefix + "_s" + secNum,
      summary_text:   secText,
      generated_by:   generatedBy,
      documents_used: JSON.stringify(documentsUsed),
    });
    await sqlWrite(`EXEC sp_SaveTurnoverAISummary @payload = '${esc(payload)}'`);
  }
}

// ── Shared SharePoint upload target resolver ────────────────────────────────────
async function resolveSpUpload(file, { turnover, graphToken, estDriveInfo, driveInfo, activeSection, getJobFolder }) {
  if (turnover.Status === "Contract Review" && estDriveInfo) {
    const year = getYear();
    const turnoverPath = `${EST_BASE_PATH}/${year} Opportunities/${turnover.ProjectName}/Turnover`;
    return await uploadToEstimatingTurnover(graphToken, estDriveInfo.driveId, turnoverPath, file);
  }
  if (!driveInfo) throw new Error("Construction SharePoint not connected");
  const jobFolder = getJobFolder();
  if (!jobFolder) throw new Error("Job number must be assigned before uploading");
  const year = turnover.JobNumber ? turnover.JobNumber.split("-")[0] : String(getYear());
  return await uploadToSharePoint(
    graphToken, driveInfo.siteId, driveInfo.driveId,
    jobFolder, year, activeSection.SectionName, file, activeSection.SectionNumber,
    turnover.ConstructionFolderItemId || null, entityBasePath(turnover.ContractingEntity)
  );
}

// Sections 17, 18 are conditional (not always required)

// Sections that use the multi-document log instead of single upload
// (18 sections total as of the 14+15 merge into "Correspondence")
const MULTI_DOC_SECTIONS = new Set([2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18]);

// Upload document types by section: docTypesFor() in config/turnoverLayout.js
// (layout-aware — a turnover's section numbers depend on its layout).

// Sections with AI capabilities
const AI_CONTRACT_REVIEW_SECTION = 2;
const CONTRACT_REVIEW_MATRIX_SECTION = 2;
const ESTIMATE_SUMMARY_SECTION = 7; // corrected from 6 — session-16 reorder moved Estimate & Budget to section 7
const SCHEDULE_SECTION = 9;
const RACI_SECTION = 6;

// Folds RaciAssignmentsJson rows into { [RaciItemID]: { [RaciPartyID]: mark } }
function buildRaciAssignMap(rows) {
  const map = {};
  for (const r of rows) {
    if (!map[r.RaciItemID]) map[r.RaciItemID] = {};
    map[r.RaciItemID][r.RaciPartyID] = r.Mark;
  }
  return map;
}
// Section 8 — uploads here run through the same Claude extraction / dedupe /
// sp_WriteQuote pipeline as the Estimating Assistant's "Add Quote" tab, so
// quotes turned over here land in the Quotes database, not just SharePoint.
// (corrected from 7 — session-16 reorder moved Vendor & Subcontractor Quotes to section 8)
const VENDOR_QUOTE_SECTION = 8;
// Section 8 — Long Lead Items structured table lives alongside the vendor quote
// document log in this same section (not a separate numbered section).
const LONG_LEAD_SECTION = 8;
// Section 14 — Correspondence (merged Scope Clarifications + Client Commitments).
// Uses the same per-document Analyze + Combined Summary engine as Contract
// Review, pointed at a separate "correspondence" system prompt category, with
// a timeline-style aggregate view instead of the contract review's flat prose.
const CORRESPONDENCE_SECTION = 14;
// Section 16 — Permits, Bonds & AHJ Requirements.
// Structured terms (responsibility dropdowns + LD text) are stored as JSON
// in TurnoverSections.Notes for this section rather than as plain text.
// Permits, Bonds & AHJ (layout 1 only, §16): sectionNumberOf(turnover, "permits").
// Financial Compliance fields the layout-2 Contract Requirements checklist
// answers instead — hidden from that layout's §4 panel so nothing is asked twice.
const REQUIREMENT_COVERED_FC_KEYS = ["BondsStatus", "LienWaiversStatus", "CertifiedPayrollRequired", "PrevailingWage", "W9Status", "COIStatus", "ExemptionCertNumber", "CertificateExpiration"];
// Section 4 — Administrative Documents: structured Financial Compliance record
// (tables in db/27_financial_compliance.sql), ported once from the estimate's
// Financial Compliance panel at turnover creation.
const FINANCIAL_COMPLIANCE_SECTION = 4;

// Admin bypass for section-ownership lock (see isAssignedOwner below) —
// SWA-ESTIMATING-ADMIN group membership via AdminContext (2026-09-11),
// replacing the hardcoded single email.
import { useIsAdmin } from "../auth/AdminContext";

// Single source of truth for the "primary action" button color —
// matches the navy used on the print preview's "Print / Save as PDF"
// button (var(--navy) / #1e3a5f in turnoverPrint.js), so every
// primary call-to-action in the app reads consistently.
// Conditional sections (often N/A): isConditionalSection() in config/turnoverLayout.js.

// Generic "what to do here" help callouts shown at the top of a section's
// document log. Sections 1 (Cover Sheet) and 4 (Administrative Documents)
// already have their own bespoke guidance blocks — everything below fills
// in the plain instructional version for sections that didn't have one yet.
const SECTION_HELP = {
  2: {
    icon: "\u270D\uFE0F",
    title: "Contract Review & Sign-Off",
    body: "Upload the proposed contract, then click Analyze to generate an AI summary. Adjust the routing defaults below if needed, then click Send for Review \u2014 each reviewer logs in to submit comments or approve.",
  },
  3: {
    icon: "\uD83D\uDCD1",
    title: "Finalized Commercial Document",
    body: "Once every department has signed off and every must-resolve contract issue is accepted, upload the signed contract (Type: Executed Contract), click Mark contract executed, then Submit for Job Number. The turnover status changes to Pending Number and no further document uploads are allowed until Accounting assigns the job number.",
  },
  5: {
    icon: "\uD83D\uDCCB",
    title: "CleanSpace Proposal",
    body: "Upload the CleanSpace proposal document that applies to this contract.",
  },
  7: { // corrected from 6 — session-16 reorder moved Estimate & Budget to section 7
    icon: "\uD83D\uDCB0",
    title: "Budget & Estimate Documents",
    body: "Upload the latest budget sheet, estimate sheet, and client breakdowns. Totals should match the contract release amount. If this is a partial release, upload both the partial release estimate and the anticipated full construction estimate.",
  },
  10: {
    icon: "\uD83D\uDCD0",
    title: "Takeoffs & Basis of Design",
    body: "Upload all takeoff information and basis of design documentation for this project.",
  },
  15: {
    icon: "\uD83D\uDDBC\uFE0F",
    title: "CAD Background",
    body: "Salespeople are responsible for obtaining CAD background from the client, or arranging designer access to the client's hosting system. Upload the background files or access instructions here.",
  },
};



// ── Main Component ─────────────────────────────────────────────────────────────
export default function TurnoverDetail() {
  const { turnoverID }          = useParams();
  const navigate                = useNavigate();
  const { instance, accounts }  = useMsal();
  const isMobile                = useIsMobile();
  const navHeight                = isMobile ? 52 : 92;
  const [mobileDetailsOpen, setMobileDetailsOpen] = useState(false);

  const [turnover, setTurnover]           = useState(null);
  const [sections, setSections]           = useState([]);
  const [actionItems, setActionItems]     = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const activeSectionRef = useRef(null); // persists across reloads
  // One automatic HubSpot sync per turnover per visit when nothing is cached
  // (or no releases): the sync at creation can fail silently (HubSpot rate
  // limits right after the readiness check), which left §7 saying the deal
  // has no releases until someone clicked Refresh HubSpot.
  const hsAutoSyncedRef = useRef(null);

  // Graph / SharePoint
  const [graphToken, setGraphToken]   = useState(null);
  const [driveInfo, setDriveInfo]     = useState(null);
  const [estDriveInfo, setEstDriveInfo] = useState(null);
  const [spError, setSpError]         = useState(null);

  // Section panel state
  const [uploading, setUploading]     = useState(false);
  const [notesText, setNotesText]     = useState("");
  const [notesDirty, setNotesDirty]   = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);

  // Action item add
  const [newAction, setNewAction]     = useState({ description: "", assigned_to: "", assigned_email: "", due_date: "" });
  const [addingAction, setAddingAction] = useState(false);

  // Documents (multi-upload sections)
  const [documents, setDocuments]       = useState([]);
  const [docUploading, setDocUploading] = useState(false);
  const [addDocForm, setAddDocForm]     = useState({ doc_type: "", description: "", doc_date: "", doc_value: "" });
  const [showAddDoc, setShowAddDoc]     = useState(false);

  // Cover sheet state
  const [hsCompanies, setHsCompanies]   = useState([]);
  const [hsContacts, setHsContacts]     = useState([]);
  const [hsLineItems, setHsLineItems]   = useState([]);
  const [hsLoading, setHsLoading]       = useState(false);
  const [hsCacheTime, setHsCacheTime]   = useState(null);

  // Schedule milestones
  const [schedule, setSchedule]           = useState([]);
  const [savingMilestone, setSavingMilestone] = useState(null); // ID being saved
  const [newMilestone, setNewMilestone]   = useState({ label: "", date: "" });
  const [addingMilestone, setAddingMilestone] = useState(false);
  const [scheduleJobStatus, setScheduleJobStatus]   = useState(null); // null | 'Processing' | 'Complete' | 'Failed'
  const scheduleJobPoll = usePolledJob();
  const [proposedMilestones, setProposedMilestones] = useState(null); // null | array from AI
  const [acceptingProposed, setAcceptingProposed] = useState(false);

  // Long lead items (Section 8 — lives alongside the vendor quote doc log)
  const [longLeadItems, setLongLeadItems]         = useState([]);
  const [savingLongLeadItem, setSavingLongLeadItem] = useState(null); // ID being saved
  const [newLongLeadItem, setNewLongLeadItem]     = useState({ name: "", vendor: "", leadTimeWeeks: "", orderByDate: "", expectedDeliveryDate: "" });
  const [addingLongLeadItem, setAddingLongLeadItem] = useState(false);

  // RACI matrix (Section 6) — dynamic parties: raciParties is an array of
  // party rows; raciAssigns maps RaciItemID → { RaciPartyID: 'R'|'A'|'C'|'I' }.
  const [raciItems, setRaciItems]           = useState([]);
  const [raciParties, setRaciParties]       = useState([]);
  const [raciAssigns, setRaciAssigns]       = useState({});
  const [savingRaciItem, setSavingRaciItem] = useState(null);   // RaciItemID being saved
  const [newRaciItem, setNewRaciItem]       = useState({ phase: "Design", category: "", activity: "" });
  const [addingRaciItem, setAddingRaciItem] = useState(false);
  const [addingRaciParty, setAddingRaciParty] = useState(false);
  const [seedingRaci, setSeedingRaci]       = useState(false);
  const [sendingRaciScope, setSendingRaciScope] = useState(false);

  // Contract review matrix
  const [contractReview, setContractReview] = useState([]);
  const [contractIssues, setContractIssues] = useState([]);
  const [contractBusy, setContractBusy]     = useState(false);
  const [contractRequirements, setContractRequirements] = useState([]);
  const [contractReviewItems, setContractReviewItems] = useState([]);
  const [changingFolder, setChangingFolder] = useState(false); // header ✏️ Change folder dialog
  const [copyingDocs, setCopyingDocs] = useState("");           // progress text while copying into the job folder
  const jobFileIndexRef = useRef(null);                         // { turnoverId, index } — job-folder files by content, for the upload duplicate check // §2 checklist answers (layout 3)
  const [marketingKickoff, setMarketingKickoff] = useState(normalizeKickoff(null)); // §18 (layout 3)
  const [savingRequirement, setSavingRequirement]     = useState(false);
  const [savingReview, setSavingReview]     = useState(null); // ReviewID being saved
  const [sendingReview, setSendingReview]   = useState(false);
  const [contractJobStatus, setContractJobStatus] = useState(null); // null | 'Processing' | 'Complete' | 'Failed'
  const contractJobPoll = usePolledJob();
  const [corrJobStatus, setCorrJobStatus] = useState(null); // null | 'Processing' | 'Complete' | 'Failed'
  const corrJobPoll = usePolledJob();
  const sendingReviewRef = useRef(false);
  // (usePolledJob clears each live poll interval on unmount, so navigating
  // away mid-job doesn't leave an interval hitting sqlRead forever.)

  const [submittingJobNumber, setSubmittingJobNumber] = useState(false);
  const [completionBlockers, setCompletionBlockers] = useState(null); // { sectionId, missing: [] } — inline "why can't I complete this" panel

  // Section 16 — Permits, Bonds & AHJ Requirements structured terms.
  // Stored as JSON in TurnoverSections.Notes for section 16 only.
  // Shape: { permits: "", bonds: "", ld: "", ld_terms: "", ahj_notes: "" }
  const PERMITS_DEFAULT = { permits: "", bonds: "", ld: "", ld_terms: "", ahj_notes: "" };
  const [permitsTerms, setPermitsTerms]     = useState(PERMITS_DEFAULT);
  const [savingPermits, setSavingPermits]   = useState(false);

  // Section 4 — Financial Compliance structured record (db/27_financial_compliance.sql).
  // Ported once from the estimate's Financial Compliance panel at turnover
  // creation, then tracked independently from here on.
  const FINANCIAL_COMPLIANCE_DEFAULT = { CertifiedPayrollRequired: false, PrevailingWage: false };
  const [financialCompliance, setFinancialCompliance] = useState(FINANCIAL_COMPLIANCE_DEFAULT);
  const [savingFinancialCompliance, setSavingFinancialCompliance] = useState(false);


  // AI analysis state
  const [analyzingDoc, setAnalyzingDoc]   = useState(null); // docID currently being analyzed

  // AI summaries
  const [aiSummaries, setAiSummaries]     = useState([]);
  const [showSummaryFlyout, setShowSummaryFlyout] = useState(null); // docID | 'combined' | null
  const [coverForm, setCoverForm]       = useState({
    project_manager: "", planned_turnover_date: "", commission_splits: [],
  });
  const [coverDirty, setCoverDirty]     = useState(false);
  const [savingCover, setSavingCover]   = useState(false);
  const [pmList, setPmList]             = useState([]); // Construction-dept subset for the PM dropdown
  const [pmListLoading, setPmListLoading] = useState(false);
  const [directoryUsers, setDirectoryUsers] = useState([]);
  // Standing contact defaults (Contract Review roles, Job Number alert,
  // Package email standing recipients) — editable on the Turnover
  // Configuration admin page (db/28_turnover_config.sql). Not turnover-scoped,
  // loaded once independent of turnoverID.
  const [turnoverConfigContacts, setTurnoverConfigContacts] = useState([]);
  const [turnoverConfigOptions, setTurnoverConfigOptions] = useState([]); // dropdown-list edits (Turnover Configuration)
  const [directoryFailed, setDirectoryFailed] = useState(false);

  const currentUser = accounts[0] ? { name: accounts[0].name, email: accounts[0].username } : null;
  const isAdmin = useIsAdmin() === true;

  // ── Section ownership lock ──────────────────────────────────────────────────
  // Only the person a section is assigned to (TurnoverSections.AssignedEmail)
  // may upload documents or toggle completion/applicability on that section.
  // Admin always overrides. Fail-open (anyone may act) when a section has no
  // AssignedEmail yet — assignment isn't guaranteed to be populated (e.g. a
  // HubSpot owner sync gap), and hard-blocking on missing data would brick a
  // section nobody could complete. Session note added 2026-07-09.
  function isAssignedOwner(section) {
    const email = currentUser?.email?.toLowerCase();
    if (isAdmin) return true;
    // §18 Marketing: Marketing (its owner), the salesperson and the PM
    // work through it together, so any of them can complete it.
    if (sectionKind(turnover, section?.SectionNumber) === "marketing"
      && canEditKickoff(turnover, currentUser, turnoverConfigContacts, isAdmin)) return true;
    if (!section?.AssignedEmail) return true;
    if (!!email && email === section.AssignedEmail.toLowerCase()) return true;
    // Estimator sections (§7–13): the Estimator owns them and is primary, but
    // the turnover's salesperson may also upload and complete them (the §7
    // estimate-to-release check still applies whoever uploads).
    return !!email && sharesWithSalesperson(turnover, section.SectionNumber)
      && email === String(turnover?.SalespersonEmail || "").toLowerCase();
  }

  // ── Load data ────────────────────────────────────────────────────────────────
  const load = useCallback(() => {
    setLoading(true);
    sqlRead(`EXEC sp_GetTurnoverDetail @turnover_id=${turnoverID}`)
      .then(result => {
        // SP returns one row with named JSON columns — parse each
        const row = result?.ResultSets?.Table1?.[0]
          || result?.Table1?.[0]
          || result?.value?.[0]
          || null;

        const safeParseJSON = (str) => {
          if (!str) return [];
          try { return JSON.parse(str); } catch { return []; }
        };
        const safeParseObj = (str) => {
          if (!str) return null;
          try { return JSON.parse(str); } catch { return null; }
        };

        const t = row?.TurnoverJson   ? safeParseObj(row.TurnoverJson)    : row;
        const s = row?.SectionsJson   ? safeParseJSON(row.SectionsJson)   : (result?.Table2 || []);
        const a = row?.ActionItemsJson? safeParseJSON(row.ActionItemsJson): (result?.Table3 || []);
        const d = row?.DocumentsJson  ? safeParseJSON(row.DocumentsJson)  : (result?.Table4 || []);

        // Load contract review matrix from Col 8
        const cr = row?.ContractReviewJson ? safeParseJSON(row.ContractReviewJson) : [];
        setContractReview(cr);
        setContractIssues(row?.ContractIssuesJson ? safeParseJSON(row.ContractIssuesJson) : []);
        setContractRequirements(row?.ContractRequirementsJson ? safeParseJSON(row.ContractRequirementsJson) : []);
        setContractReviewItems(row?.ContractReviewItemsJson ? safeParseJSON(row.ContractReviewItemsJson) : []);
        setMarketingKickoff(normalizeKickoff(row?.MarketingKickoffJson ? safeParseObj(row.MarketingKickoffJson)?.DataJson : null));

        // Load AI summaries from Col 7
        const ai = row?.AISummariesJson ? safeParseJSON(row.AISummariesJson) : [];
        setAiSummaries(ai);

        // Load schedule milestones from Col 6
        const sc = row?.ScheduleJson ? safeParseJSON(row.ScheduleJson) : [];
        setSchedule(sc);

        // Load any pending AI schedule proposals (survive page navigation)
        const rawProposals = row?.ScheduleProposalsJson ? safeParseJSON(row.ScheduleProposalsJson) : [];
        if (rawProposals.length > 0) {
          const normalized = rawProposals.map((p, i) => ({
            _id:         `proposal_${p.TurnoverScheduleProposalID || i}`,
            _accepted:   p.IsCSRelevant !== 0,
            label:       p.Label,
            start_date:  p.StartDate,
            end_date:    p.EndDate,
            notes:       p.Notes,
            cs_relevant: p.IsCSRelevant !== 0,
          }));
          setProposedMilestones(normalized);
        }

        // Load long lead items (Section 8) from LongLeadItemsJson
        const lli = row?.LongLeadItemsJson ? safeParseJSON(row.LongLeadItemsJson) : [];
        setLongLeadItems(lli);

        // Load RACI matrix (Section 6) from RaciItemsJson / RaciPartiesJson /
        // RaciAssignmentsJson (assignments fold into an itemId → partyId → mark map)
        setRaciItems(row?.RaciItemsJson ? safeParseJSON(row.RaciItemsJson) : []);
        setRaciParties(row?.RaciPartiesJson ? safeParseJSON(row.RaciPartiesJson) : []);
        setRaciAssigns(buildRaciAssignMap(row?.RaciAssignmentsJson ? safeParseJSON(row.RaciAssignmentsJson) : []));

        // Load section 16 permits terms from that section's Notes field (stored as JSON)
        // Layout 1 only — layout 2 folded permits into §4 Contract Requirements.
        const permitsNum = sectionNumberOf(t, "permits");
        const sec16 = permitsNum ? s.find(sec => sec.SectionNumber === permitsNum) : null;
        if (sec16?.Notes) {
          try {
            const parsed = JSON.parse(sec16.Notes);
            if (parsed && typeof parsed === "object" && "permits" in parsed) {
              setPermitsTerms(parsed);
            }
          } catch { /* Notes is plain text from before this feature — leave defaults */ }
        }

        // Load Financial Compliance (Section 4) from FinancialComplianceJson
        const fc = row?.FinancialComplianceJson ? safeParseObj(row.FinancialComplianceJson) : null;
        setFinancialCompliance(fc ? { ...FINANCIAL_COMPLIANCE_DEFAULT, ...fc } : FINANCIAL_COMPLIANCE_DEFAULT);

        // Load HubSpot cache from SQL (Col 5)
        const hsCache = row?.HubSpotCacheJson ? safeParseObj(row.HubSpotCacheJson) : null;
        if (hsCache) {
          const cachedCompanies  = safeParseJSON(hsCache.CompaniesJson);
          const cachedContacts   = safeParseJSON(hsCache.ContactsJson);
          const cachedLineItems  = safeParseJSON(hsCache.LineItemsJson);
          if (cachedCompanies.length)  setHsCompanies(cachedCompanies);
          if (cachedContacts.length)   setHsContacts(cachedContacts);
          if (cachedLineItems.length)  setHsLineItems(cachedLineItems);
          setHsCacheTime(hsCache.LastRefreshedAt || null);
        }
        const cachedReleases = hsCache ? safeParseJSON(hsCache.LineItemsJson) : [];
        if (t?.HubSpotDealID && cachedReleases.length === 0 && hsAutoSyncedRef.current !== t.TurnoverID) {
          hsAutoSyncedRef.current = t.TurnoverID;
          loadHubSpotCompanies(t.HubSpotDealID);
        }

        setTurnover(t || null);
        setSections(s);
        setActionItems(a);
        setDocuments(d);
        if (t) {
          let splits = safeParseJSON(t.CommissionSplitsJson);
          if (!splits.length) {
            // Pre-populate with the deal owner (+ BD2, if present) as a starting
            // point. This is purely a UI default — nothing is written to SQL
            // until the user actually edits and saves the Commission section.
            const defaults = [];
            if (t.Salesperson) {
              defaults.push({ id: Date.now() + Math.random(), name: t.Salesperson, role: "Sales", split_pct: "" });
            }
            if (t.SalesRep2) {
              defaults.push({ id: Date.now() + Math.random() + 0.001, name: t.SalesRep2, role: "Sales", split_pct: "" });
            }
            splits = defaults;
          }
          setCoverForm({
            project_manager:       t.ProjectManager       || "",
            planned_turnover_date: t.PlannedTurnoverDate  ? t.PlannedTurnoverDate.split("T")[0] : "",
            commission_splits:     splits,
            public_reference:         t.PublicReference         || "",
            confidentiality_notes:    t.ConfidentialityNotes    || "",
            photography_restrictions: t.PhotographyRestrictions || "",
          });
        }
        // Restore previously active section, or default to first
        if (s.length > 0) {
          const prevId = activeSectionRef.current;
          const restored = prevId ? s.find(sec => sec.SectionID === prevId) : null;
          // ?section=N deep link (e.g. the contract review email opens on §2) —
          // honored on first load only; afterwards the user's own navigation wins.
          const linkedNum = !prevId && !activeSection
            ? Number.parseInt(new URLSearchParams(window.location.search).get("section"), 10)
            : NaN;
          const linked = Number.isInteger(linkedNum) ? s.find(sec => sec.SectionNumber === linkedNum) : null;
          const toActivate = restored || (activeSection ? s.find(sec => sec.SectionID === activeSection.SectionID) : null) || linked || s[0];
          if (toActivate) {
            setActiveSection(toActivate);
            if (!restored && !activeSection) setNotesText(toActivate.Notes || "");
          }
        }
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [turnoverID]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // ── Init Graph token for Construction SP ─────────────────────────────────────
  useEffect(() => {
    if (!accounts?.length) return;
    getGraphToken(instance, accounts)
      .then(async token => {
        setGraphToken(token);
        // Initialize both SP sites in parallel — failure of one doesn't affect the other
        const [constResult, estResult] = await Promise.allSettled([
          getConstructionDrive(token),
          getEstimatingDrive(token),
        ]);
        if (constResult.status === "fulfilled") {
          setDriveInfo(constResult.value);
        } else {
          setSpError(`Construction SharePoint connection failed: ${constResult.reason?.message}`);
        }
        if (estResult.status === "fulfilled") {
          setEstDriveInfo(estResult.value);
        } else {
          console.warn("Estimating SP init failed:", estResult.reason?.message);
          // Non-blocking — Estimating SP only needed during Contract Review
        }
      })
      .catch(e => setSpError(`SharePoint token failed: ${e.message}`));
  }, [instance, accounts]);

  // ── Save the job folder when it isn't saved yet (2026-09-29) ─────────────────
  // A numbered turnover with no saved folder: find the one in the prime
  // entity's <year> Jobs filed under its job number and save it, so uploads,
  // the header strip and the copy all use that folder whatever it is named.
  // Once per turnover per visit; a miss or failure changes nothing.
  const folderHealRef = useRef(null);
  useEffect(() => {
    if (!turnover?.TurnoverID || !turnover.JobNumber || turnover.ConstructionFolderItemId) return;
    if (!graphToken || !driveInfo || folderHealRef.current === turnover.TurnoverID) return;
    folderHealRef.current = turnover.TurnoverID;
    (async () => {
      try {
        const folder = await locateJobFolder(graphToken, driveInfo.driveId, turnover);
        if (!folder?.id || !folder.webUrl) return;
        const res = await sqlWrite(`EXEC sp_SetTurnoverFolder @payload = '${payloadOf(setFolderPayload(turnover.TurnoverID, folder))}'`);
        const out = res?.ResultSets?.Table1?.[0];
        if (out && Number(out.Result) === 1) {
          setTurnover(t => (t && t.TurnoverID === out.TurnoverID ? { ...t, SharePointFolderUrl: out.SharePointFolderUrl, ConstructionFolderItemId: out.ConstructionFolderItemId } : t));
        }
      } catch (e) {
        console.warn("Couldn't look up the job folder:", e.message);
      }
    })();
  }, [turnover, graphToken, driveInfo]);

  // ── Load standing contact defaults once (not turnover-scoped) ─────────────────
  useEffect(() => {
    sqlRead("EXEC sp_GetTurnoverConfigContacts")
      .then(result => setTurnoverConfigContacts(result?.Table1 || []))
      .catch(e => console.warn("Turnover config contacts load failed — using hardcoded fallbacks:", e.message));
    sqlRead("EXEC sp_GetTurnoverConfigOptions")
      .then(result => setTurnoverConfigOptions(result?.Table1 || []))
      .catch(e => console.warn("Turnover dropdown lists load failed — using built-in options:", e.message));
  }, []);

  // ── Fetch O365 directory once — the full unfiltered list serves section   ──
  // ── reassignment, contract review Assigned To, and action item assignment.──
  // ── The Cover Sheet PM dropdown uses the Construction-department subset.  ──
  // ──                                                                       ──
  // ── SCOPE NOTE: `department` (and `jobTitle`) are NOT in the property set ──
  // ── User.ReadBasic.All exposes — Graph silently returns them as null, it  ──
  // ── never errors. Reading them requires User.Read.All (admin consent).    ──
  // ── We try a dedicated User.Read.All token first; until consent is        ──
  // ── granted in Entra that fails silently and we fall back to the shared   ──
  // ── basic token — names/emails still populate, department comes back null,──
  // ── and the PM dropdown falls back to the full list.                      ──
  useEffect(() => {
    if (!graphToken || !accounts[0]) return;
    setPmListLoading(true);
    setDirectoryFailed(false);
    (async () => {
      let dirToken = graphToken;
      try {
        const res = await instance.acquireTokenSilent({ scopes: ["User.Read.All"], account: accounts[0] });
        dirToken = res.accessToken;
      } catch {
        console.warn("User.Read.All not consented — department filtering unavailable; PM dropdown will show full directory. Admin: grant User.Read.All consent in Entra.");
      }
      try {
        const r = await fetch(
          `https://graph.microsoft.com/v1.0/users?$select=${DIRECTORY_SELECT}&$top=999`,
          { headers: { Authorization: `Bearer ${dirToken}` } }
        );
        if (!r.ok) throw new Error(`Graph ${r.status}`);
        const data = await r.json();
        // People only — rooms, shared mailboxes, guests and disabled accounts are dropped.
        const everyone = toDirectoryUsers(data.value);
        setDirectoryUsers(everyone);
        if (everyone.length === 0) setDirectoryFailed(true);

        const construction = everyone.filter(u => u.department.toLowerCase().includes("construction"));
        // Falls back to the full directory when department is unreadable
        // (User.Read.All not yet consented) or unpopulated in AAD.
        setPmList(construction.length > 0 ? construction : everyone);
      } catch {
        setDirectoryFailed(true);
      } finally {
        setPmListLoading(false);
      }
    })();
  }, [graphToken, instance, accounts]);

  // ── Section selection ────────────────────────────────────────────────────────
  function selectSection(sec) {
    if (notesDirty) {
      if (!window.confirm("You have unsaved notes. Discard changes?")) return;
      setNotesDirty(false);
    }
    setActiveSection(sec);
    activeSectionRef.current = sec?.SectionID || null;
    setNotesText(sec?.Notes || "");
    setShowAddDoc(false); // reset add doc form on section change
    // Fetch HubSpot company/contact data when Cover Sheet selected
    if (sec.SectionNumber === 1 && turnover?.HubSpotDealID && hsCompanies.length === 0) {
      loadHubSpotCompanies(turnover.HubSpotDealID);
    }
  }

  // Fetches companies/contacts/line items for the linked HubSpot deal and
  // writes them to TurnoverHubSpotCache so they're available on the next
  // page load without another round trip. Core fetch/mapping/cache-write
  // logic lives in utils/hubspotSync.js, shared with the New Turnover
  // creation flow (Turnovers.jsx) so a freshly created turnover already
  // has this data cached the first time anyone opens it.
  async function loadHubSpotCompanies(dealId) {
    if (!dealId) return;
    setHsLoading(true);
    try {
      const result = await syncHubSpotData(dealId, turnoverID, currentUser);
      if (!result) return;

      setHsContacts(result.contacts);
      setHsCompanies(result.companies);
      setHsLineItems(result.lineItems);

      if (result.refreshedAt) setHsCacheTime(result.refreshedAt);
      if (result.dealDescription !== null) {
        setTurnover(t => t ? { ...t, DealDescription: result.dealDescription } : t);
      }
      if (result.squareFeet !== null && result.squareFeet !== undefined) {
        setTurnover(t => t ? { ...t, SquareFeet: result.squareFeet } : t);
      }
      if (result.cacheError) {
        // Data still displays below — only the cache write failed, so the
        // person sees current HubSpot data but should know it may not
        // persist until they retry.
        console.warn("HubSpot cache write failed:", result.cacheError);
        alert("HubSpot data loaded, but saving it failed: " + result.cacheError + "\nYou may need to click Refresh HubSpot again.");
      }
    } catch (e) {
      console.error("HubSpot data fetch failed:", e);
      alert("Failed to load HubSpot data: " + e.message);
    } finally {
      setHsLoading(false);
    }
  }

  // ── Build SharePoint job folder name ─────────────────────────────────────────
  function getJobFolder() {
    if (!turnover) return null;
    // "2026-201 - <deal name without its location>" (folderPath.js jobFolderName).
    // Only used when no folder is saved — an existing one is found by job
    // number and saved on open (the effect below).
    if (turnover.JobNumber && turnover.ProjectName) {
      return jobFolderName(turnover.JobNumber, turnover.ProjectName);
    }
    return null;
  }

  // ── Turnover Package — printable summary + email ────────────────────────────
  // Opens a styled HTML view in a new tab; Print dialog "Save as PDF" produces
  // the distributable file. The tab's "Email Package" panel posts recipients
  // back to this page (which holds the MSAL session), and we send via Graph
  // with the full package attached as an HTML file.
  const packageEmailHandler = useRef(null);

  useEffect(() => {
    function listener(e) {
      // Only accept messages from our own origin — this handler sends email via
      // the user's Graph token with recipients taken from the message, so a
      // foreign window must never be able to drive it. The print popup is an
      // about:blank document written by this page, so it inherits our origin.
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === "estassist-send-package" && packageEmailHandler.current) {
        packageEmailHandler.current(e);
      }
    }
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, []);

  // Made async so the Mail.Send token can be warmed here, as a direct
  // continuation of the "Print Package" button's own click — a genuine user
  // gesture in this window. Previously the token was only requested later,
  // inside packageEmailHandler, which runs off a "message" event from the
  // print-preview popup rather than a direct click in this window. When
  // acquireTokenSilent happens to miss (token expired, cache cleared, etc.)
  // at that point, the acquireTokenPopup fallback is an interactive popup
  // triggered from a non-gesture context, which modern browsers increasingly
  // block outright — a plausible explanation for email silently stopping
  // working with no app code change at all (browser popup-gesture policy has
  // simply gotten stricter over time). Warming the cache here means the later
  // silent acquisition in packageEmailHandler hits cache far more reliably.
  // Added 2026-07-09.
  async function generatePackagePdf() {
    if (!turnover) return;

    try {
      await getMailToken(instance, accounts, loginScopes);
    } catch (e) {
      console.warn("Could not pre-acquire Mail.Send token before opening the package:", e.message);
      // Continue anyway — the later send attempt will retry and surface its own error.
    }

    // Standing recipients for the package email panel — Turnover Configuration
    // admin page (TurnoverConfigContacts, GroupKey='package_email'). Falls
    // back to buildTurnoverPackageHtml's own hardcoded default when empty.
    const standingRecipients = turnoverConfigContacts
      .filter(c => c.GroupKey === "package_email" && c.Email)
      .map(c => ({ name: c.Name, email: c.Email, dept: "Standing" }));
    // Layout 3: the turnover meeting covers the Marketing section, so the
    // Marketing Team is on the email and the invite automatically.
    if (hasMarketingKickoff(turnover)) {
      const seen = new Set(standingRecipients.map(r => r.email.toLowerCase()));
      for (const c of turnoverConfigContacts) {
        if (c.GroupKey !== "marketing_team" || !c.Email || seen.has(c.Email.toLowerCase())) continue;
        seen.add(c.Email.toLowerCase());
        standingRecipients.push({ name: c.Name || c.Email, email: c.Email, dept: "Marketing" });
      }
    }

    const packageData = {
      turnover, sections, documents, actionItems, schedule,
      contractReview, aiSummaries, hsCompanies, hsContacts, hsLineItems,
      permitsTerms, contractRequirements, contractIssues, contractReviewItems, generatedBy: currentUser?.name || "", directoryUsers,
      marketingKickoff, optionRows: turnoverConfigOptions,
      standingRecipients: standingRecipients.length > 0 ? standingRecipients : undefined,
    };

    // (Re)register the email handler with current state captured
    packageEmailHandler.current = async (event) => {
      const { to = [], note = "" } = event.data || {};
      try {
        const token = await getMailToken(instance, accounts, loginScopes);

        // The package is read on its own page in the Turnover Engine
        // (/turnover/:id/package) — no attachment (2026-09-24).
        const platformUrl = `https://estimating.cleanspaceus.com/turnover/${turnoverID}`;
        const packageUrl = `${platformUrl}/package`;
        const amount = turnover.ContractValue || turnover.DealAmount;

        const bodyHtml = `
<div style="font-family:Arial,sans-serif;max-width:640px;color:#111;">
  <div style="background:#1e3a5f;padding:18px 24px;border-radius:8px 8px 0 0;">
    <div style="color:#fff;font-size:17px;font-weight:700;">Turnover Package</div>
    <div style="color:#93c5fd;font-size:13px;margin-top:3px;">${turnover.JobNumber ? turnover.JobNumber + " — " : ""}${turnover.ProjectName || ""}</div>
  </div>
  <div style="border:1px solid #e5e7eb;border-top:none;border-radius:0 0 8px 8px;padding:22px 24px;">
    ${note ? `<p style="margin:0 0 14px;font-size:14px;white-space:pre-wrap;">${note.replace(/</g, "&lt;")}</p>` : ""}
    <p style="margin:0 0 14px;font-size:14px;">The turnover package for <strong>${turnover.ProjectName || ""}</strong> is ready. Open it in the Turnover Engine and work down it section by section — click any document to see it without leaving the page. Please review it before the turnover meeting.</p>
    <table style="width:100%;border-collapse:collapse;margin-bottom:18px;font-size:13px;">
      <tr><td style="padding:5px 12px;background:#f8f9fb;font-weight:600;width:150px;border:1px solid #e5e7eb;">Job #</td><td style="padding:5px 12px;border:1px solid #e5e7eb;">${turnover.JobNumber || "Pending"}</td></tr>
      <tr><td style="padding:5px 12px;background:#f8f9fb;font-weight:600;border:1px solid #e5e7eb;">Client</td><td style="padding:5px 12px;border:1px solid #e5e7eb;">${turnover.ClientName || "—"}</td></tr>
      <tr><td style="padding:5px 12px;background:#f8f9fb;font-weight:600;border:1px solid #e5e7eb;">Contract Value</td><td style="padding:5px 12px;border:1px solid #e5e7eb;">${amount ? "$" + parseFloat(amount).toLocaleString() : "—"}</td></tr>
      <tr><td style="padding:5px 12px;background:#f8f9fb;font-weight:600;border:1px solid #e5e7eb;">Project Manager</td><td style="padding:5px 12px;border:1px solid #e5e7eb;">${turnover.ProjectManager || "—"}</td></tr>
      <tr><td style="padding:5px 12px;background:#f8f9fb;font-weight:600;border:1px solid #e5e7eb;">Planned Turnover</td><td style="padding:5px 12px;border:1px solid #e5e7eb;">${turnover.PlannedTurnoverDate ? parseLocalDate(turnover.PlannedTurnoverDate).toLocaleDateString("en-US", { timeZone: "America/New_York", month: "long", day: "numeric", year: "numeric" }) : "—"}</td></tr>
    </table>
    <div style="margin-bottom:14px;">
      <a href="${packageUrl}" style="display:inline-block;background:#1e3a5f;color:#fff;padding:9px 20px;border-radius:6px;font-size:13px;font-weight:700;text-decoration:none;">Open in Turnover Engine →</a>
    </div>
    <p style="margin:0;font-size:12px;color:#6b7280;">Sent from the Turnover Engine by ${currentUser?.name || ""}.</p>
  </div>
</div>`;

        const res = await fetch(`${GRAPH_BASE}/me/sendMail`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token.accessToken}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            message: {
              subject: `Turnover Package — ${turnover.JobNumber ? turnover.JobNumber + " " : ""}${turnover.ProjectName || ""}`,
              body: { contentType: "HTML", content: bodyHtml },
              toRecipients: to.map(r => ({ emailAddress: { address: r.email, name: r.name || r.email } })),
            },
            saveToSentItems: true,
          }),
        });
        if (!res.ok) {
          const errText = await res.text().catch(() => "");
          throw new Error(`Graph ${res.status}${errText ? " — " + errText.slice(0, 120) : ""}`);
        }

        // Calendar invite — created separately after the email sends successfully.
        // Uses Graph createEvent on the sender's calendar with all email recipients
        // as attendees. Best-effort: if it fails the email still went.
        const { meeting } = event.data || {};
        if (meeting?.date) {
          try {
            // The meeting date/time inputs are plain wall-clock values with no
            // timezone attached (e.g. "09:00") — CleanSpace is Eastern-based and
            // the Graph event below is always tagged timeZone: "America/New_York",
            // so the correct behavior is to treat whatever was typed as literal
            // Eastern time, unconditionally, regardless of the sender's own
            // browser/OS timezone.
            //
            // Previously this ran the typed string through `new Date(...)`
            // (which JS interprets using the BROWSER's local timezone, not
            // Eastern) and then `.toISOString()` (which converts to UTC) before
            // handing the result to Graph still labeled "America/New_York" —
            // silently shifting every invite by the sender's UTC offset (e.g. a
            // sender on Eastern time typing 9:00 AM got a 1:00 PM invite; other
            // timezones were wrong by a different amount). Fixed 2026-07-09 by
            // doing all arithmetic in a timezone-agnostic UTC-component space
            // (via Date.UTC) and emitting the result as a bare wall-clock string
            // with no conversion, exactly matching what Graph expects when paired
            // with an explicit timeZone.
            const [startH, startM] = (meeting.start || "09:00").split(":").map(Number);
            const [y, mo, d] = meeting.date.split("-").map(Number);
            const pad = n => String(n).padStart(2, "0");
            const wallClock = (totalMinutesFromMidnight) => {
              // Date.UTC/getUTC* here are just a pure calendar calculator —
              // never reinterpreted through the runtime's local timezone.
              const dt = new Date(Date.UTC(y, mo - 1, d, 0, totalMinutesFromMidnight, 0));
              return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}T${pad(dt.getUTCHours())}:${pad(dt.getUTCMinutes())}:00`;
            };
            const startWallClock = wallClock(startH * 60 + startM);
            const endWallClock   = wallClock(startH * 60 + startM + (meeting.duration || 120));

            const kickoffNum = sectionNumberOf(turnover, "marketing");
            const agenda = kickoffNum
              ? `<p><strong>Agenda</strong></p><ol><li>Turnover package walkthrough</li><li>Marketing (Section ${kickoffNum}) — signage, content opportunities, project sheet and photo milestones (<a href="${platformUrl}?section=${kickoffNum}">Section ${kickoffNum}</a>)</li></ol>`
              : "";
            const eventBody = `<p>The turnover package for <strong>${turnover.ProjectName || ""}</strong> (Job ${turnover.JobNumber || "TBD"}) has been issued — <a href="${packageUrl}">open it in the Turnover Engine</a> and review it before the meeting.</p>${agenda}${meeting.note ? `<p>${meeting.note}</p>` : ""}`;
            const eventRes = await fetch(`${GRAPH_BASE}/me/events`, {
              method: "POST",
              headers: { Authorization: `Bearer ${token.accessToken}`, "Content-Type": "application/json" },
              body: JSON.stringify({
                subject: `Turnover Meeting — ${turnover.JobNumber ? turnover.JobNumber + " " : ""}${turnover.ProjectName || ""}`,
                body: { contentType: "HTML", content: eventBody },
                start: { dateTime: startWallClock, timeZone: "America/New_York" },
                end:   { dateTime: endWallClock,   timeZone: "America/New_York" },
                location: meeting.location ? { displayName: meeting.location } : undefined,
                attendees: to.map(r => ({
                  emailAddress: { address: r.email, name: r.name || r.email },
                  type: "required",
                })),
                isOnlineMeeting: false,
                responseRequested: true,
              }),
            });
            // Record it so §18 and the turnover show when the meeting is.
            if (eventRes.ok) {
              const mtg = payloadOf({ turnover_id: parseInt(turnoverID, 10), meeting_at: startWallClock, location: meeting.location || "" });
              sqlWrite(`EXEC sp_RecordTurnoverMeeting @payload = '${mtg}'`)
                .then(() => setTurnover(t => (t ? { ...t, TurnoverMeetingAt: startWallClock, TurnoverMeetingLocation: meeting.location || null } : t)))
                .catch(e => console.warn("Recording the turnover meeting failed:", e.message));
            }
          } catch (calErr) {
            console.warn("Calendar invite failed (email sent successfully):", calErr.message);
            // Still report overall success — email went through
          }
        }

        event.source?.postMessage({
          type: "estassist-package-email-result", ok: true, count: to.length,
          calendarCreated: !!(meeting?.date),
        }, event.origin);
      } catch (err) {
        event.source?.postMessage({ type: "estassist-package-email-result", ok: false, error: err.message }, event.origin);
      }
    };

    openTurnoverPackagePrint(packageData);

    // Save the HTML package to SharePoint for the record (best-effort).
    if (graphToken) {
      try {
        const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
        const safeName = (s) => (s || "").replace(/[/\\:*?"<>|]/g, "-").slice(0, 60);
        const fileName = `Turnover Package - ${safeName(turnover.JobNumber || "NoJob")} - ${safeName(turnover.ProjectName)} - ${today}.html`;
        const htmlStr  = buildTurnoverPackageHtml(packageData);
        const htmlFile = new File([htmlStr], fileName, { type: "text/html" });
        if (turnover.Status === "Contract Review" && estDriveInfo) {
          const year = getYear();
          const path = `${EST_BASE_PATH}/${year} Opportunities/${turnover.ProjectName}/Turnover`;
          await uploadToEstimatingTurnover(graphToken, estDriveInfo.driveId, path, htmlFile);
        } else if (driveInfo && getJobFolder()) {
          const year = turnover.JobNumber ? turnover.JobNumber.split("-")[0] : String(getYear());
          await uploadToSharePoint(
            graphToken, driveInfo.siteId, driveInfo.driveId,
            getJobFolder(), year, "", htmlFile, 0,
            turnover.ConstructionFolderItemId || null, entityBasePath(turnover.ContractingEntity)
          );
        }
      } catch (spErr) {
        console.warn("[package] SharePoint save skipped:", spErr.message);
      }
    }
  }

  // ── Multi-document upload ────────────────────────────────────────────────────
  // meta overrides addDocForm — lets batch uploads pass distinct metadata per file
  // without racing the shared addDocForm state.
  // Job-folder files by content fingerprint, read once per turnover and kept
  // current as files are uploaded (duplicateFiles.js).
  async function jobFileIndex() {
    const cached = jobFileIndexRef.current;
    if (cached && cached.turnoverId === turnover.TurnoverID) return cached.index;
    const folderId = await findJobFolderId(graphToken, driveInfo.driveId, turnover);
    if (!folderId) return null;
    const { files } = await listFolderFilesDeep(graphToken, driveInfo.driveId, folderId);
    const index = buildFileIndex(files);
    jobFileIndexRef.current = { turnoverId: turnover.TurnoverID, index };
    return index;
  }

  async function handleDocUpload(file, meta) {
    const docMeta = meta || addDocForm;
    if (!graphToken) { alert("SharePoint not connected."); return; }
    // During Contract Review, skip job folder check — uploads go to Estimating SP
    let fileHash = null;
    if (turnover.Status !== "Contract Review") {
      const jobFolder = getJobFolder();
      if (!jobFolder) { alert("Job number must be assigned before uploading documents."); return; }
      // Same file already somewhere in the job folder (any folder, any name)?
      // Warn; the user can still upload. A failed check never blocks an upload.
      if (driveInfo) {
        let dup = null;
        try {
          const index = await jobFileIndex();
          fileHash = await quickXorHashOfFile(file);
          dup = index ? findDuplicate(index, fileHash) : null;
        } catch (e) {
          console.warn("[upload] duplicate check skipped:", e.message);
        }
        if (dup && !window.confirm(uploadDuplicatePrompt(file.name, dup))) {
          throw new Error(`Not uploaded — already in the job folder at ${duplicateLocation(dup)}`);
        }
      }
    }
    setDocUploading(true);
    try {
      const result = await resolveSpUpload(file, { turnover, graphToken, estDriveInfo, driveInfo, activeSection, getJobFolder });
      const payload = JSON.stringify({
        turnover_id:   parseInt(turnoverID),
        section_id:    activeSection.SectionID,
        doc_type:      docMeta.doc_type   || null,
        description:   docMeta.description || null,
        doc_date:      docMeta.doc_date    || null,
        doc_value:     docMeta.doc_value   || null,
        hubspot_line_item_id: docMeta.hubspot_line_item_id || null,
        sharepoint_url: result.webUrl,
        file_name:     result.name,
        uploaded_by:   currentUser?.name || "",
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_SaveTurnoverDocument @payload = '${payload}'`);
      const cached = jobFileIndexRef.current;
      if (fileHash && cached?.turnoverId === turnover.TurnoverID) {
        addToIndex(cached.index, { id: result.webUrl, name: result.name, webUrl: result.webUrl, hash: fileHash, path: `Turnover/${String(activeSection.SectionNumber).padStart(2, "0")} - ${activeSection.SectionName}/${result.name}` });
      }
      setAddDocForm({ doc_type: "", description: "", doc_date: "", doc_value: "" });
      setShowAddDoc(false);
      load();
      return result; // { webUrl, name } — used by quote pipeline for source_file_url
    } catch (e) {
      alert("Upload failed: " + e.message);
      throw e; // re-throw so batch upload callers can track per-file failures
    } finally {
      setDocUploading(false);
    }
  }


  // ── Combined summary across all analyzed docs ─────────────────────────────────
  async function runCombinedSummary(sectionNumber = 2, promptCategory = "contract_review", typePrefixBase = "contract_review") {
    if (analyzingDoc) return;
    const targetSection = sections.find(s => s.SectionNumber === sectionNumber);
    if (!targetSection) return;

    // Gather all per-doc summaries
    const docSummaries = documents
      .filter(d => d.SectionID === targetSection.SectionID)
      .map(d => {
        const typePrefix = `${typePrefixBase}_doc_${d.TurnoverDocumentID}`;
        const secs = aiSummaries
          .filter(s => s.SummaryType?.startsWith(typePrefix + "_s"))
          .sort(bySummaryNum)
          .map(s => s.SummaryText).join("\n\n");
        return secs ? `## Document: ${d.FileName}${d.DocDate ? ` (${d.DocDate})` : ""}\n\n${secs}` : null;
      })
      .filter(Boolean);

    if (docSummaries.length === 0) { alert("No individual document summaries found. Analyze each document first."); return; }

    setAnalyzingDoc("combined");
    try {
      const promptRes = await sqlRead(`EXEC sp_GetSystemPrompt @category='${promptCategory}'`);
      const promptRow = promptRes?.Table1?.[0] || promptRes?.value?.[0] || {};
      const defaultPrompt = promptCategory === CORRESPONDENCE_PROMPT_CATEGORY
        ? "You are CleanSpace LLC's internal estimating reviewer."
        : "You are CleanSpace LLC's internal contract reviewer.";
      const systemPrompt = promptRow?.FullPrompt || defaultPrompt;

      const synthesisAsk = promptCategory === CORRESPONDENCE_PROMPT_CATEGORY
        ? `Now synthesize these individual reviews into a single consolidated correspondence summary, ordered chronologically by document date where available. Combine and deduplicate findings, call out client commitments and open items clearly, and flag anything that contradicts the executed contract or proposal scope.`
        : `Now synthesize these individual reviews into a single consolidated contract review. Combine and deduplicate findings, highlight the most critical risk flags across all documents, and provide unified recommendations for each department. Use the same section structure as the individual reviews.`;

      const userPrompt = `You have reviewed the following documents individually for project: ${turnover.ProjectName}

${docSummaries.join("\n\n---\n\n")}

${synthesisAsk}`;

      const text = await callClaude(systemPrompt, userPrompt, 4000);
      if (!text) throw new Error("AI returned no content");

      const typePrefix = `${typePrefixBase}_combined`;
      await saveAISummaryParts(turnoverID, targetSection.SectionID, typePrefix, text, documents.filter(d => d.SectionID === targetSection.SectionID).map(d => d.FileName), currentUser?.name || "");
      load();
      setShowSummaryFlyout(`combined_${typePrefixBase}`);
    } catch (e) {
      alert("Combined summary failed: " + e.message);
    } finally {
      setAnalyzingDoc(null);
    }
  }

  // Change an uploaded document's Type. The upload form leaves Type optional and
  // the file is immutable, so this is how an untyped upload (e.g. the signed
  // contract, which §3 only accepts as "Executed Contract") gets its type.
  // sp_SaveTurnoverDocument's update path changes metadata only.
  async function handleSetDocType(doc, docType) {
    try {
      const payload = JSON.stringify({
        turnover_document_id: doc.TurnoverDocumentID,
        turnover_id: parseInt(turnoverID),
        doc_type: docType,
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_SaveTurnoverDocument @payload = '${payload}'`);
      await load();
    } catch (e) {
      alert("Could not change the document type: " + e.message);
    }
  }

  async function handleDeleteDoc(docId) {
    if (!window.confirm("Remove this document?")) return;
    try {
      const payload = JSON.stringify({ turnover_document_id: docId }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_DeleteTurnoverDocument @payload = '${payload}'`);
      load();
    } catch (e) {
      alert("Error: " + e.message);
    }
  }


  // ── Contract review sign-off ─────────────────────────────────────────────────
  async function saveContractReview(row) {
    setSavingReview(row.ReviewID);
    try {
      const payload = JSON.stringify({
        review_id:      row.ReviewID,
        turnover_id:    parseInt(turnoverID),
        assigned_to:    row.AssignedTo    || null,
        assigned_email: row.AssignedEmail || null,
        status:         row.Status,
        reviewer_name:  row.ReviewerName  || null,
        reviewer_email: row.ReviewerEmail || null,
        reviewed_at:    row.ReviewedAt    || null,
        comments:       row.Comments      || null,
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_SaveContractReview @payload = '${payload}'`);
      // Update local state
      setContractReview(prev => prev.map(r =>
        r.ReviewID === row.ReviewID ? { ...r, ...row } : r
      ));
      // Sync section IsComplete with matrix status — auto-mark or un-mark as needed
      const updated = contractReview.map(r => r.ReviewID === row.ReviewID ? { ...r, ...row } : r);
      const allDone = updated.every(r => r.Status === 'Approved' || r.Status === 'Approved with Comments');
      const rejected = updated.some(r => r.Status === 'Rejected');
      const sec = sections.find(s => s.SectionNumber === 2);
      if (sec) {
        if (allDone && !rejected && !sec.IsComplete) {
          const sPayload = JSON.stringify({ section_id: sec.SectionID, turnover_id: parseInt(turnoverID), is_complete: 1 }).replace(/'/g, "''");
          await sqlWrite(`EXEC sp_UpdateTurnoverSection @payload = '${sPayload}'`);
          load();
        } else if ((!allDone || rejected) && sec.IsComplete) {
          const sPayload = JSON.stringify({ section_id: sec.SectionID, turnover_id: parseInt(turnoverID), is_complete: 0 }).replace(/'/g, "''");
          await sqlWrite(`EXEC sp_UpdateTurnoverSection @payload = '${sPayload}'`);
          load();
        }
      }
    } catch (e) {
      alert("Error saving review: " + e.message);
    } finally {
      setSavingReview(null);
    }
  }

  function updateReviewRow(reviewId, field, value) {
    setContractReview(prev => prev.map(r =>
      r.ReviewID === reviewId ? { ...r, [field]: value } : r
    ));
  }

  // ── Notify Accounting that a turnover is ready for job number assignment ──────
  // `exception` ({ reason, expectedDate, by }) = requested without an executed
  // contract: the email says so instead of claiming the contract is executed.
  async function notifyJobNumberSubmitted(exception = null) {
    try {
      const token = await getMailToken(instance, accounts, loginScopes);

      const platformUrl = `https://estimating.cleanspaceus.com/turnover/${turnoverID}`;
      const dealAmount = turnover.ContractValue
        ? "$" + parseFloat(turnover.ContractValue).toLocaleString()
        : (turnover.DealAmount ? "$" + parseFloat(turnover.DealAmount).toLocaleString() : "—");

      // Standing recipients come from the Turnover Configuration admin page
      // (TurnoverConfigContacts, GroupKey='job_number_alert') — falls back to
      // the hardcoded defaults if that hasn't loaded or the table is empty.
      const configTo = turnoverConfigContacts.find(c => c.GroupKey === "job_number_alert" && c.RoleLabel === "To" && c.Email);
      const configCC = turnoverConfigContacts.filter(c => c.GroupKey === "job_number_alert" && c.RoleLabel === "CC" && c.Email);
      const notifyTo = configTo ? { name: configTo.Name, email: configTo.Email } : JOB_NUMBER_NOTIFY_TO;
      const notifyCC = configCC.length > 0
        ? configCC.map(c => ({ name: c.Name, email: c.Email }))
        : JOB_NUMBER_NOTIFY_CC;

      const toRecipients = [{ emailAddress: { address: notifyTo.email, name: notifyTo.name } }];
      const ccRecipients = [...notifyCC];
      if (turnover.SalespersonEmail) {
        ccRecipients.push({ name: turnover.Salesperson || turnover.SalespersonEmail, email: turnover.SalespersonEmail });
      }

      const htmlBody = `
<div style="font-family: Arial, sans-serif; max-width: 680px; color: #111;">
  <div style="background: #1e3a5f; padding: 20px 28px; border-radius: 8px 8px 0 0;">
    <div style="color: #fff; font-size: 18px; font-weight: 700;">Job Number Requested</div>
    <div style="color: #93c5fd; font-size: 13px; margin-top: 4px;">${turnover.ProjectName}</div>
  </div>
  <div style="border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 8px 8px; padding: 24px 28px;">
    <p style="margin: 0 0 16px; font-size: 14px;">Hi Nicole,</p>
    <p style="margin: 0 0 16px; font-size: 14px;">
      ${exception
        ? "A job number has been requested for the project below. The contract has NOT been executed yet; it will follow."
        : "The contract for the project below has been executed and the turnover is ready for job number assignment."}
    </p>
    ${exception ? exceptionNoteHtml(exception) : ""}
    <div style="background: #fef3c7; border: 1px solid #f59e0b; border-radius: 6px; padding: 10px 14px; margin-bottom: 16px; font-size: 13px; color: #92400e; font-weight: 600;">
      ⏱ Please assign the job number within 24 hours of this email.
    </div>
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 13px;">
      <tr><td style="padding: 6px 12px; background: #f8f9fb; font-weight: 600; width: 160px; border: 1px solid #e5e7eb;">Project</td><td style="padding: 6px 12px; border: 1px solid #e5e7eb;">${turnover.ProjectName}</td></tr>
      <tr><td style="padding: 6px 12px; background: #f8f9fb; font-weight: 600; border: 1px solid #e5e7eb;">Client</td><td style="padding: 6px 12px; border: 1px solid #e5e7eb;">${turnover.ClientName || "—"}</td></tr>
      <tr><td style="padding: 6px 12px; background: #f8f9fb; font-weight: 600; border: 1px solid #e5e7eb;">Contract Value</td><td style="padding: 6px 12px; border: 1px solid #e5e7eb;">${dealAmount}</td></tr>
      <tr><td style="padding: 6px 12px; background: #f8f9fb; font-weight: 600; border: 1px solid #e5e7eb;">Sales Lead</td><td style="padding: 6px 12px; border: 1px solid #e5e7eb;">${turnover.Salesperson || "—"}</td></tr>
      <tr><td style="padding: 6px 12px; background: #f8f9fb; font-weight: 600; border: 1px solid #e5e7eb;">HubSpot Deal #</td><td style="padding: 6px 12px; border: 1px solid #e5e7eb;">${turnover.HubSpotDealID || "—"}</td></tr>
    </table>
    <div style="margin-bottom: 20px;">
      <a href="${assignJobNumberUrl(turnoverID)}" style="display: inline-block; background: #1e3a5f; color: #fff; padding: 10px 22px; border-radius: 6px; font-size: 13px; font-weight: 700; text-decoration: none;">
        Assign Job Number →
      </a>
      <div style="margin-top: 8px; font-size: 12px; color: #6b7280;">Opens the Turnovers list with this turnover's Assign Job Number dialog. <a href="${platformUrl}" style="color: #1d4ed8;">View the turnover</a></div>
    </div>
    <p style="margin: 0; font-size: 12px; color: #6b7280;">
      Document uploads for this turnover are locked until a job number is assigned in EstAssist. Questions? Reply to this email.
    </p>
  </div>
</div>`;

      await fetch(`${GRAPH_BASE}/me/sendMail`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: {
            subject: `Job Number Requested${exception ? " — EXECUTED CONTRACT TO FOLLOW" : ""} (Assign within 24hr) — ${turnover.ProjectName}`,
            body: { contentType: "HTML", content: htmlBody },
            toRecipients,
            ccRecipients: ccRecipients.map(r => ({ emailAddress: { address: r.email, name: r.name } })),
          },
          saveToSentItems: true,
        }),
      });
    } catch (e) {
      console.error("[Job Number Notify] Failed to send:", e);
      alert("Submitted for job number, but the notification email failed to send: " + e.message);
    }
  }

  // ── Contract issues & execution (the gate between review and signing) ──────
  // Rules: turnover/contractIssues.js; enforced again by sp_SaveContractIssue /
  // sp_MarkContractExecuted / sp_SubmitForJobNumber. Emails are best-effort.
  async function sendTurnoverMail(to, subject, html, cc = []) {
    const recipients = (to || []).filter(r => r?.email);
    if (recipients.length === 0) return;
    const ccRecipients = (cc || []).filter(r => r?.email);
    try {
      const token = await getMailToken(instance, accounts, loginScopes);
      await fetch(`${GRAPH_BASE}/me/sendMail`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token.accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          message: {
            subject, body: { contentType: "HTML", content: html },
            toRecipients: recipients.map(r => ({ emailAddress: { address: r.email, name: r.name || r.email } })),
            ccRecipients: ccRecipients.map(r => ({ emailAddress: { address: r.email, name: r.name || r.email } })),
          },
          saveToSentItems: true,
        }),
      });
    } catch (e) {
      console.warn("Contract issue notification failed (non-blocking):", e.message);
    }
  }

  async function runContractIssue(payload) {
    setContractBusy(true);
    try {
      const body = JSON.stringify(payload).replace(/'/g, "''");
      const res = await sqlWrite(`EXEC sp_SaveContractIssue @payload = '${body}'`);
      const row = res?.ResultSets?.Table1?.[0] || res?.Table1?.[0];
      if (!row || row.Result !== 1) { alert(row?.Error || "The contract issue could not be saved."); return null; }
      load();
      return row;
    } catch (e) {
      alert("Error: " + e.message);
      return null;
    } finally {
      setContractBusy(false);
    }
  }

  // Copy files uploaded before the job number (still in the Estimating site)
  // into the job folder — header folder strip; moveDocsToJobFolder.js.
  async function copyPendingDocs() {
    if (!graphToken || !driveInfo) return;
    setCopyingDocs("Starting…");
    try {
      const result = await copyDocsToJobFolder({
        token: graphToken, constDriveId: driveInfo.driveId, turnover, documents, sections,
        onProgress: setCopyingDocs,
      });
      alert(copySummary(result));
      jobFileIndexRef.current = null;
      load();
    } catch (e) {
      alert("The files weren't copied: " + e.message);
    } finally {
      setCopyingDocs("");
    }
  }

  // §2 checklist: save one item's answer (null clears).
  async function saveReviewItem(review, item, answer) {
    try {
      const res = await sqlWrite(`EXEC sp_SaveContractReviewItem @payload = '${payloadOf(reviewItemPayload(review.ReviewID, item, answer, currentUser))}'`);
      const out = res?.ResultSets?.Table1?.[0];
      if (!out || Number(out.Result) !== 1) throw new Error(out?.Error || "The answer was not saved.");
      setContractReviewItems(prev => {
        const rest = prev.filter(a => !(a.ReviewID === review.ReviewID && a.ItemKey === item.key));
        return out.Answer ? [...rest, out] : rest;
      });
    } catch (e) {
      alert("Error saving the checklist: " + e.message);
    }
  }

  const contractIssueHandlers = {
    create: async (review, description, mustResolve) =>
      !!(await runContractIssue(createIssuePayload(parseInt(turnoverID), review.ReviewID, description, mustResolve, currentUser))),
    remove: async (issue) => {
      if (!window.confirm("Delete this issue?")) return false;
      return !!(await runContractIssue(deleteIssuePayload(parseInt(turnoverID), issue.IssueID)));
    },
    resolve: async (issue, outcome, notes) => {
      const ok = await runContractIssue(resolveIssuePayload(parseInt(turnoverID), issue.IssueID, outcome, notes, currentUser));
      if (!ok) return false;
      const review = contractReview.find(r => r.ReviewID === issue.ReviewID);
      const mail = outcomeRecordedEmail({ turnover, review, issue, outcome, notes, resolvedBy: currentUser?.name });
      await sendTurnoverMail([{ name: review?.AssignedTo, email: review?.AssignedEmail }], mail.subject, mail.html);
      return true;
    },
    accept: async (issue) => {
      const ok = await runContractIssue(acceptIssuePayload(parseInt(turnoverID), issue.IssueID, currentUser));
      if (!ok) return false;
      // Last outstanding must-resolve issue accepted, and nothing else holding
      // the contract: tell Sales it can be signed.
      const after = contractIssues.map(i => (i.IssueID === issue.IssueID ? { ...i, Status: "accepted" } : i));
      const gate = contractGate({ reviews: contractReview, issues: after, turnover });
      if (gate.readyToExecute) {
        const mail = readyToSignEmail({ turnover, acceptedCount: gate.accepted.length });
        await sendTurnoverMail([{ name: turnover.Salesperson, email: turnover.SalespersonEmail }], mail.subject, mail.html);
      }
      return true;
    },
    reopen: async (issue, note) => {
      const ok = await runContractIssue(reopenIssuePayload(parseInt(turnoverID), issue.IssueID, note, currentUser));
      if (!ok) return false;
      const review = contractReview.find(r => r.ReviewID === issue.ReviewID);
      const mail = issueReopenedEmail({ turnover, review, issue, note, reopenedBy: currentUser?.name });
      await sendTurnoverMail([{ name: turnover.Salesperson, email: turnover.SalespersonEmail }], mail.subject, mail.html);
      return true;
    },
  };

  async function runContractExecution(payload) {
    setContractBusy(true);
    try {
      const body = JSON.stringify(payload).replace(/'/g, "''");
      const res = await sqlWrite(`EXEC sp_MarkContractExecuted @payload = '${body}'`);
      const row = res?.ResultSets?.Table1?.[0] || res?.Table1?.[0];
      if (!row || row.Result !== 1) { alert(row?.Error || "The contract execution could not be recorded."); return; }
      load();
    } catch (e) {
      alert("Error: " + e.message);
    } finally {
      setContractBusy(false);
    }
  }

  async function handleSubmitJobNumber() {
    if (submittingJobNumber) return;
    if (!turnover?.ContractExecutedDate) {
      alert("Record the executed contract on Section 3 (Mark contract executed) before submitting for a job number.");
      return;
    }
    if (!window.confirm("Submit this turnover for job number assignment?")) return;
    setSubmittingJobNumber(true);
    try {
      const payload = JSON.stringify({ turnover_id: parseInt(turnoverID) }).replace(/'/g, "''");
      const res = await sqlWrite(`EXEC sp_SubmitForJobNumber @payload = '${payload}'`);
      const row = res?.ResultSets?.Table1?.[0] || res?.Table1?.[0];
      if (row && row.Status !== "Pending Number") {
        alert("The job number request was not submitted — the contract must be recorded as executed while the turnover is in Contract Review.");
        load();
        return;
      }
      await notifyJobNumberSubmitted();
      await sendPmPreAlert();
      load();
    } catch (e) { alert("Error: " + e.message); }
    finally { setSubmittingJobNumber(false); }
  }

  // Pre-alert the VP of Construction (PM Assignment Alert list on Turnover
  // Configuration) with the project description, to assign the PM.
  async function sendPmPreAlert() {
    const pm = pmAlertRecipients(turnoverConfigContacts);
    if (pm.to.length > 0) {
      const mail = buildPmAssignmentEmail({ turnover, submittedBy: currentUser?.name });
      await sendTurnoverMail(pm.to, mail.subject, mail.html, pm.cc);
    }
  }

  // Administrator-only: request the job number before the executed contract
  // exists (sp_SubmitForJobNumberException — also enforced on the SQL proxy).
  // Accounting is emailed with the reason and expected date; Section 3 stays open.
  async function handleJobNumberException({ reason, expectedDate }) {
    if (submittingJobNumber) return;
    if (!isAdmin) { alert("Only an administrator can request a job number without an executed contract."); return; }
    if (!window.confirm("Request the job number WITHOUT an executed contract?\n\nAccounting will be told the contract is still to follow, with your reason and the expected date. Section 3 stays open until the signed contract is recorded.")) return;
    setSubmittingJobNumber(true);
    try {
      const payload = JSON.stringify(jobNumberExceptionPayload(parseInt(turnoverID), { reason, expectedDate }, currentUser)).replace(/'/g, "''");
      const res = await sqlWrite(`EXEC sp_SubmitForJobNumberException @payload = '${payload}'`);
      const row = res?.ResultSets?.Table1?.[0] || res?.Table1?.[0];
      if (!row || Number(row.Result) !== 1) {
        alert(row?.Error || "The job number request was not submitted.");
        load();
        return;
      }
      await notifyJobNumberSubmitted({ reason, expectedDate, by: currentUser?.name });
      await sendPmPreAlert();
      load();
    } catch (e) { alert("Error: " + e.message); }
    finally { setSubmittingJobNumber(false); }
  }

  // ── Send for Review: kick off background AI analysis + one summary email ────
  // Replaces the old per-reviewer synchronous sendMail loop. This now just
  // Poll job status until Complete/Failed, then update state
  function startJobPolling(turnoverId, sectionId) {
    contractJobPoll.start(async (stop) => {
      const payload = JSON.stringify({ turnover_id: turnoverId, section_id: sectionId }).replace(/'/g, "''");
      const res = await sqlRead(`EXEC sp_GetContractAnalysisJobStatus @payload = '${payload}'`);
      const row = res?.Table1?.[0] || res?.value?.[0];
      if (!row) return;
      if (row.Status === "Complete" || row.Status === "Failed") {
        setContractJobStatus(row.Status);
        stop();
        // Refresh AI summaries so flyout has latest data
        if (row.Status === "Complete") {
          try {
            const refreshRes = await sqlRead(`EXEC sp_GetTurnoverDetail @turnover_id=${turnoverId}`);
            const refreshRow = refreshRes?.Table1?.[0] || refreshRes?.value?.[0];
            if (refreshRow?.AISummariesJson) {
              const ai = JSON.parse(refreshRow.AISummariesJson);
              setAiSummaries(Array.isArray(ai) ? ai : []);
            }
          } catch {}
        }
      }
    }, 5000);
  }

  // Analyze button — runs analysis only, no email
  async function runContractAnalyzeOnly(doc) {
    const section2 = sections.find(s => s.SectionNumber === 2);
    if (!section2) return;

    setAnalyzingDoc(doc.TurnoverDocumentID);
    setContractJobStatus("Processing");
    try {
      const jobId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
      const payload = {
        turnoverId:  parseInt(turnoverID),
        sectionId:   section2.SectionID,
        documents:   [{ sharePointUrl: doc.SharePointUrl }],
        submittedBy: { name: currentUser?.name || "", email: currentUser?.email || "" },
        jobId,
        analyzeOnly: true,
      };

      const res = await fetch(CONTRACT_ANALYSIS_URL, {
        method:  "POST",
        headers: { "Content-Type": "application/json", ...(await authHeader()) },
        body:    JSON.stringify(payload),
      });
      if (!res.ok && res.status !== 202) throw new Error(`Request failed (${res.status})`);

      // Start polling for completion
      startJobPolling(parseInt(turnoverID), section2.SectionID);
    } catch (e) {
      alert("Failed to start analysis: " + e.message);
      setContractJobStatus(null);
    } finally {
      setAnalyzingDoc(null);
    }
  }

  // Correspondence polling — mirrors startJobPolling but targets corrJob state
  function startCorrJobPolling(turnoverId, sectionId) {
    corrJobPoll.start(async (stop) => {
      const payload = JSON.stringify({ turnover_id: turnoverId, section_id: sectionId }).replace(/'/g, "''");
      const res = await sqlRead(`EXEC sp_GetContractAnalysisJobStatus @payload = '${payload}'`);
      const row = res?.Table1?.[0] || res?.value?.[0];
      if (!row) return;
      if (row.Status === "Complete" || row.Status === "Failed") {
        setCorrJobStatus(row.Status);
        stop();
        if (row.Status === "Complete") {
          try {
            const refreshRes = await sqlRead(`EXEC sp_GetTurnoverDetail @turnover_id=${turnoverId}`);
            const refreshRow = refreshRes?.Table1?.[0] || refreshRes?.value?.[0];
            if (refreshRow?.AISummariesJson) {
              const ai = JSON.parse(refreshRow.AISummariesJson);
              setAiSummaries(Array.isArray(ai) ? ai : []);
            }
          } catch {}
        }
      }
    }, 5000);
  }

  // Send a correspondence doc to the correspondence-api-lx backend for analysis
  async function analyzeDocViaBackend(doc) {
    const corrSection = sections.find(s => s.SectionNumber === CORRESPONDENCE_SECTION);
    if (!corrSection) return;

    setAnalyzingDoc(doc.TurnoverDocumentID);
    setCorrJobStatus("Processing");
    try {
      const jobId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);

      const promptRes = await sqlRead(`EXEC sp_GetSystemPrompt @category='${CORRESPONDENCE_PROMPT_CATEGORY}'`);
      const promptRow = promptRes?.Table1?.[0] || promptRes?.value?.[0] || {};
      const systemPrompt = promptRow?.FullPrompt || "You are CleanSpace LLC's internal estimating reviewer. Review the provided correspondence and extract key decisions, scope clarifications, exclusions, assumptions, and client commitments with dates where available.";

      const contractValue = turnover.ContractValue || turnover.DealAmount;
      const baseContext = `PROJECT CONTEXT:\n- Project: ${turnover.ProjectName}\n- Client: ${turnover.ClientName || "Not specified"}\n- Contract Value: ${contractValue ? "$" + parseFloat(contractValue).toLocaleString() : "Not specified"}\n- Document: ${doc.FileName}`;

      const payload = {
        turnoverId:    parseInt(turnoverID),
        sectionId:     corrSection.SectionID,
        documentId:    doc.TurnoverDocumentID,
        sharePointUrl: doc.SharePointUrl,
        fileName:      doc.FileName,
        jobId,
        systemPrompt,
        baseContext,
        submittedBy: { name: currentUser?.name || "", email: currentUser?.email || "" },
      };

      const res = await fetch(CORRESPONDENCE_API_URL, {
        method:  "POST",
        headers: { "Content-Type": "application/json", ...(await authHeader()) },
        body:    JSON.stringify(payload),
      });
      if (!res.ok && res.status !== 200 && res.status !== 202) throw new Error(`Request failed (${res.status})`);

      startCorrJobPolling(parseInt(turnoverID), corrSection.SectionID);
    } catch (e) {
      alert("Failed to start correspondence analysis: " + e.message);
      setCorrJobStatus(null);
    } finally {
      setAnalyzingDoc(null);
    }
  }

  // Send for Review — if analysis already done, email only; otherwise full pipeline
  async function sendContractReview() {
    if (sendingReviewRef.current) return;
    sendingReviewRef.current = true;
    // Every exit before a send starts must release the guard, or the button
    // silently does nothing until the page is reloaded.
    const bail = () => { sendingReviewRef.current = false; };

    const missingEmail = contractReview.filter(r => !r.AssignedEmail?.trim());
    if (missingEmail.length > 0) {
      alert(`Missing email for: ${missingEmail.map(r => r.Department).join(", ")}`);
      bail(); return;
    }

    const section2 = sections.find(s => s.SectionNumber === 2);
    if (!section2) { alert("Section 2 not found — cannot send."); bail(); return; }

    const contractDocs = documents.filter(d => d.SectionID === section2.SectionID && d.SharePointUrl);
    if (contractDocs.length === 0) {
      alert("Upload the contract document to Section 2 before sending for review.");
      bail(); return;
    }

    const recipients = contractReview.map(r => ({
      department:     r.Department,
      name:           r.AssignedTo || r.AssignedEmail,
      email:          r.AssignedEmail,
      responsibility: r.Responsibility || "",
    }));
    const submittedBy = { name: currentUser?.name || "", email: currentUser?.email || "" };

    // Check if analysis already exists
    const hasSummary = aiSummaries.some(
      s => s.SectionID === section2.SectionID && s.SummaryType?.startsWith("contract_review_combined_s")
    );

    if (hasSummary) {
      if (!window.confirm(
        `Analysis already complete. Send the existing summary email to ${contractReview.length} contacts?`
      )) { bail(); return; }

      setSendingReview(true);
      try {
        // Build HTML from existing summaries in state
        const sectionTitles = ["Contract Overview", "Risk Flags", "Action Items", "Obligations & Deadlines"];
        const combinedSections = aiSummaries
          .filter(s => s.SectionID === section2.SectionID && s.SummaryType?.startsWith("contract_review_combined_s"))
          .sort((a, b) => {
            const na = parseInt(a.SummaryType.match(/s(\d+)$/)?.[1] || 0);
            const nb = parseInt(b.SummaryType.match(/s(\d+)$/)?.[1] || 0);
            return na - nb;
          });

        const fileLinks = documents
          .filter(d => d.SectionID === section2.SectionID && d.SharePointUrl)
          .map(d => d.SharePointUrl);

        // Reviewer instructions first — the summary alone told nobody what to do.
        const combinedHtml = buildReviewInstructionsHtml({
          turnoverId: turnoverID, projectName: turnover.ProjectName, clientName: turnover.ClientName,
          submittedBy: submittedBy.name, reviewers: recipients, fileLinks,
        }) + `<div style="font-family:Arial,sans-serif;font-size:14px;line-height:1.6;max-width:800px">` +
          combinedSections.map((s, i) => {
            let md = s.SummaryText || "";
            // strip old JSON wrapper if present
            const j = md.indexOf("{");
            if (j !== -1) { try { const p = JSON.parse(md.slice(j, md.lastIndexOf("}") + 1)); if (p.markdown) md = p.markdown; } catch {} }
            md = md.replace(/\\n/g, "\n");
            // Render through the shared HTML-escaping renderer — this markdown is
            // AI output derived from an uploaded contract (attacker-supplied), and
            // this HTML goes out in an email. Never build it with raw regexes here.
            const html = contractMarkdownToEmailHtml(md);
            const title = sectionTitles[i] || `Section ${i + 1}`;
            return `<div style="margin-bottom:28px"><h2 style="color:#1e3a5f;font-size:16px;border-bottom:2px solid #e5e7eb;padding-bottom:6px;margin-bottom:12px">${title}</h2>${html}</div>`;
          }).join("") + `</div>`;

        const res = await fetch(CONTRACT_COMPLETE_URL, {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({
            jobId:       "email-only",
            recipients,
            submittedBy,
            combinedHtml,
            projectName: turnover.ProjectName || "",
            jobNumber:   turnover.JobNumber   || turnover.DealID || "",
            fileLinks,
          }),
        });
        if (!res.ok && res.status !== 202) throw new Error(`Request failed (${res.status})`);
        alert(`Email sent to ${contractReview.length} contacts.`);
      } catch (e) {
        alert("Failed to send email: " + e.message);
      } finally {
        sendingReviewRef.current = false;
        setSendingReview(false);
      }
      return;
    }

    // No existing summary — run full pipeline
    if (!window.confirm(
      `Send for review to ${contractReview.length} contacts? This starts an AI contract analysis — one summary email goes out to everyone once it's ready.`
    )) { bail(); return; }

    setSendingReview(true);
    try {
      const jobId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
      const payload = {
        turnoverId:  parseInt(turnoverID),
        sectionId:   section2.SectionID,
        documents:   contractDocs.map(d => ({ sharePointUrl: d.SharePointUrl })),
        recipients,
        submittedBy,
        jobId,
      };

      const res = await fetch(CONTRACT_ANALYSIS_URL, {
        method:  "POST",
        headers: { "Content-Type": "application/json", ...(await authHeader()) },
        body:    JSON.stringify(payload),
      });
      if (!res.ok && res.status !== 202) throw new Error(`Analysis request failed (${res.status})`);

      setContractJobStatus("Processing");
      startJobPolling(parseInt(turnoverID), section2.SectionID);
      alert(`Started — one summary email will go out to ${contractReview.length} contacts once analysis is ready.`);
    } catch (e) {
      alert("Failed to start review: " + e.message);
    } finally {
      sendingReviewRef.current = false;
      setSendingReview(false);
    }
  }

  // ── Schedule milestone save ──────────────────────────────────────────────────
  async function saveMilestone(milestone) {
    setSavingMilestone(milestone.TurnoverScheduleID);
    try {
      const payload = JSON.stringify({
        turnover_schedule_id: milestone.TurnoverScheduleID,
        turnover_id:          parseInt(turnoverID),
        milestone_label:      milestone.MilestoneLabel,
        milestone_date:       milestone.MilestoneDate || null,
        notes:                milestone.Notes || null,
        is_custom:            milestone.IsCustom ? 1 : 0,
        display_order:        milestone.DisplayOrder,
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_SaveTurnoverMilestone @payload = '${payload}'`);
      // Update local state only — no full reload needed
      setSchedule(prev => prev.map(m =>
        m.TurnoverScheduleID === milestone.TurnoverScheduleID
          ? { ...m, MilestoneLabel: milestone.MilestoneLabel, MilestoneDate: milestone.MilestoneDate, Notes: milestone.Notes }
          : m
      ));
    } catch (e) {
      alert("Error saving milestone: " + e.message);
    } finally {
      setSavingMilestone(null);
    }
  }

  async function deleteMilestone(milestone) {
    const msg = milestone.IsCustom
      ? `Remove "${milestone.MilestoneLabel}"?`
      : `Remove "${milestone.MilestoneLabel}" from the schedule?\n\nThis is a standard milestone — it won't be restored unless you recreate the turnover. Continue?`;
    if (!window.confirm(msg)) return;
    try {
      const payload = JSON.stringify({ turnover_schedule_id: milestone.TurnoverScheduleID, turnover_id: parseInt(turnoverID), is_delete: 1 }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_SaveTurnoverMilestone @payload = '${payload}'`);
      setSchedule(prev => prev.filter(m => m.TurnoverScheduleID !== milestone.TurnoverScheduleID));
    } catch (e) { alert("Error: " + e.message); }
  }

  async function addCustomMilestone() {
    if (!newMilestone.label.trim()) return;
    setAddingMilestone(true);
    try {
      const maxOrder = schedule.length > 0 ? Math.max(...schedule.map(s => s.DisplayOrder)) : 100;
      const payload = JSON.stringify({
        turnover_id:    parseInt(turnoverID),
        milestone_label: newMilestone.label.trim(),
        milestone_date:  newMilestone.date || null,
        is_custom:       1,
        display_order:   maxOrder + 10,
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_SaveTurnoverMilestone @payload = '${payload}'`);
      // Reload just the schedule from SQL to get the new ID
      const schedRes = await sqlRead(`EXEC sp_GetTurnoverDetail @turnover_id=${turnoverID}`);
      const schedRow = schedRes?.ResultSets?.Table1?.[0] || schedRes?.Table1?.[0] || schedRes?.value?.[0] || null;
      if (schedRow?.ScheduleJson) {
        setSchedule(JSON.parse(schedRow.ScheduleJson));
      }
      setNewMilestone({ label: "", date: "" });
    } catch (e) { alert("Error: " + e.message); }
    finally { setAddingMilestone(false); }
  }

  // ── Schedule extraction — submits PDF to cs-est-schedule-api background job ──
  async function extractScheduleMilestones(file) {
    if (scheduleJobStatus === 'Processing') return;
    if (!SCHEDULE_ANALYSIS_URL) {
      alert("Schedule analysis service not configured (VITE_SCHEDULE_ANALYSIS_URL missing).");
      return;
    }
    try {
      const base64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload  = () => res(r.result.split(",")[1]);
        r.onerror = () => rej(new Error("FileReader failed"));
        r.readAsDataURL(file);
      });

      // Save the schedule PDF to SharePoint and record it as a Section 9 document.
      // Best-effort — a SharePoint failure doesn't block the analysis.
      if (graphToken && activeSection) {
        try {
          const spResult = await resolveSpUpload(file, { turnover, graphToken, estDriveInfo, driveInfo, activeSection, getJobFolder });
          const docPayload = JSON.stringify({
            turnover_id:    parseInt(turnoverID),
            section_id:     activeSection.SectionID,
            doc_type:       "GC Schedule",
            description:    null,
            doc_date:       null,
            doc_value:      null,
            sharepoint_url: spResult.webUrl,
            file_name:      spResult.name,
            uploaded_by:    currentUser?.name || "",
          }).replace(/'/g, "''");
          await sqlWrite(`EXEC sp_SaveTurnoverDocument @payload = '${docPayload}'`);
          load();
        } catch (spErr) {
          console.warn("[schedule] SharePoint upload skipped:", spErr.message);
        }
      }

      const res = await fetch(SCHEDULE_ANALYSIS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeader()) },
        body: JSON.stringify({
          turnover_id:         parseInt(turnoverID),
          pdf_base64:          base64,
          submitted_by_name:   currentUser?.name  || null,
          submitted_by_email:  currentUser?.email || null,
        }),
      });
      if (!res.ok) {
        const err = await res.text().catch(() => "");
        throw new Error(`Schedule API ${res.status}: ${err.slice(0, 200)}`);
      }

      setScheduleJobStatus("Processing");
      startSchedulePolling();
    } catch (e) {
      alert("Schedule analysis failed to start: " + e.message);
    }
  }

  function startSchedulePolling() {
    scheduleJobPoll.start(async (stop) => {
      try {
        const payload = JSON.stringify({ turnover_id: parseInt(turnoverID) }).replace(/'/g, "''");
        const res = await sqlRead(`EXEC sp_GetScheduleAnalysisJobStatus @payload = '${payload}'`);
        const row = res?.ResultSets?.Table1?.[0] || res?.Table1?.[0] || res?.value?.[0] || null;
        if (!row) return;

        if (row.Status === "Complete") {
          stop();
          setScheduleJobStatus("Complete");
          // Reload detail to pick up the proposals that were saved to DB
          load();
        } else if (row.Status === "Failed") {
          stop();
          setScheduleJobStatus("Failed");
          alert("Schedule analysis failed: " + (row.ErrorMessage || "Unknown error"));
        }
        // else still Processing — keep polling
      } catch (e) {
        console.warn("[schedule poll] error:", e.message);
      }
    }, 8000);
  }

  // Discard proposals — clears from DB and React state
  async function discardProposedMilestones() {
    setProposedMilestones(null);
    setScheduleJobStatus(null);
    try {
      const payload = JSON.stringify({ turnover_id: parseInt(turnoverID) }).replace(/'/g, "''");
      await sqlRead(`EXEC sp_ClearScheduleProposals @payload = '${payload}'`);
    } catch (e) {
      console.warn("[schedule] discard clear failed:", e.message);
    }
  }

  async function acceptProposedMilestones(selected) {
    if (!selected.length) return;
    setAcceptingProposed(true);
    try {
      const maxOrder = schedule.length > 0 ? Math.max(...schedule.map(s => s.DisplayOrder)) : 100;
      for (let i = 0; i < selected.length; i++) {
        const m = selected[i];
        const payload = JSON.stringify({
          turnover_id:     parseInt(turnoverID),
          milestone_label: m.label,
          milestone_date:  m.end_date || m.start_date || null,
          notes:           m.notes || null,
          is_custom:       1,
          is_cs_relevant:  m.cs_relevant !== false ? 1 : 0,
          display_order:   maxOrder + (i + 1) * 10,
        }).replace(/'/g, "''");
        await sqlWrite(`EXEC sp_SaveTurnoverMilestone @payload = '${payload}'`);
      }
      const schedRes = await sqlRead(`EXEC sp_GetTurnoverDetail @turnover_id=${turnoverID}`);
      const schedRow = schedRes?.ResultSets?.Table1?.[0] || schedRes?.Table1?.[0] || schedRes?.value?.[0] || null;
      if (schedRow?.ScheduleJson) setSchedule(JSON.parse(schedRow.ScheduleJson));
      setProposedMilestones(null);
      setScheduleJobStatus(null);
      // Clear DB proposals now that they've been accepted
      try {
        const cp = JSON.stringify({ turnover_id: parseInt(turnoverID) }).replace(/'/g, "''");
        await sqlRead(`EXEC sp_ClearScheduleProposals @payload = '${cp}'`);
      } catch (e) { console.warn("[schedule] clear proposals failed:", e.message); }
    } catch (e) {
      alert("Error saving milestones: " + e.message);
    } finally {
      setAcceptingProposed(false);
    }
  }

  // ── Long lead items save/delete/add (Section 8) ─────────────────────────────
  async function saveLongLeadItem(item) {
    setSavingLongLeadItem(item.TurnoverLongLeadItemID);
    try {
      const payload = JSON.stringify({
        turnover_long_lead_item_id: item.TurnoverLongLeadItemID,
        turnover_id:                parseInt(turnoverID),
        item_name:                  item.ItemName,
        vendor:                     item.Vendor || null,
        lead_time_weeks:            item.LeadTimeWeeks || null,
        order_by_date:              item.OrderByDate || null,
        expected_delivery_date:     item.ExpectedDeliveryDate || null,
        is_custom:                  item.IsCustom ? 1 : 0,
        display_order:              item.DisplayOrder,
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_SaveTurnoverLongLeadItem @payload = '${payload}'`);
      // Update local state only — no full reload needed
      setLongLeadItems(prev => prev.map(li =>
        li.TurnoverLongLeadItemID === item.TurnoverLongLeadItemID
          ? { ...li, ItemName: item.ItemName, Vendor: item.Vendor, LeadTimeWeeks: item.LeadTimeWeeks, OrderByDate: item.OrderByDate, ExpectedDeliveryDate: item.ExpectedDeliveryDate }
          : li
      ));
    } catch (e) {
      alert("Error saving long lead item: " + e.message);
    } finally {
      setSavingLongLeadItem(null);
    }
  }

  async function deleteLongLeadItem(item) {
    const msg = item.IsCustom
      ? `Remove "${item.ItemName}"?`
      : `Remove "${item.ItemName}" from long lead items?\n\nThis is a standard item — it won't be restored unless you recreate the turnover. Continue?`;
    if (!window.confirm(msg)) return;
    try {
      const payload = JSON.stringify({ turnover_long_lead_item_id: item.TurnoverLongLeadItemID, turnover_id: parseInt(turnoverID), is_delete: 1 }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_SaveTurnoverLongLeadItem @payload = '${payload}'`);
      setLongLeadItems(prev => prev.filter(li => li.TurnoverLongLeadItemID !== item.TurnoverLongLeadItemID));
    } catch (e) { alert("Error: " + e.message); }
  }

  async function addCustomLongLeadItem() {
    if (!newLongLeadItem.name.trim()) return;
    setAddingLongLeadItem(true);
    try {
      const maxOrder = longLeadItems.length > 0 ? Math.max(...longLeadItems.map(li => li.DisplayOrder)) : 100;
      const payload = JSON.stringify({
        turnover_id:            parseInt(turnoverID),
        item_name:              newLongLeadItem.name.trim(),
        vendor:                 newLongLeadItem.vendor.trim() || null,
        lead_time_weeks:        newLongLeadItem.leadTimeWeeks ? parseInt(newLongLeadItem.leadTimeWeeks) : null,
        order_by_date:          newLongLeadItem.orderByDate || null,
        expected_delivery_date: newLongLeadItem.expectedDeliveryDate || null,
        is_custom:              1,
        display_order:          maxOrder + 10,
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_SaveTurnoverLongLeadItem @payload = '${payload}'`);
      // Reload just the long lead items from SQL to get the new ID
      const lliRes = await sqlRead(`EXEC sp_GetTurnoverDetail @turnover_id=${turnoverID}`);
      const lliRow = lliRes?.ResultSets?.Table1?.[0] || lliRes?.Table1?.[0] || lliRes?.value?.[0] || null;
      if (lliRow?.LongLeadItemsJson) {
        setLongLeadItems(JSON.parse(lliRow.LongLeadItemsJson));
      }
      setNewLongLeadItem({ name: "", vendor: "", leadTimeWeeks: "", orderByDate: "", expectedDeliveryDate: "" });
    } catch (e) { alert("Error: " + e.message); }
    finally { setAddingLongLeadItem(false); }
  }

  // ── RACI matrix (Section 6) ───────────────────────────────────────────────────
  async function saveRaciItem(item) {
    setSavingRaciItem(item.RaciItemID);
    try {
      const payload = JSON.stringify({
        raci_item_id: item.RaciItemID,
        turnover_id:  parseInt(turnoverID),
        phase:        item.Phase,
        category:     item.Category || null,
        activity:     item.Activity,
        notes:        item.Notes    || null,
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_SaveTurnoverRaciItem @payload = '${payload}'`);
      setRaciItems(prev => prev.map(r => r.RaciItemID === item.RaciItemID ? item : r));
    } catch (e) {
      alert("Error saving RACI item: " + e.message);
    } finally {
      setSavingRaciItem(null);
    }
  }

  // One cell = (item, party). Optimistic update; the proc deletes the
  // assignment row when the cycled mark comes back around to blank.
  async function cycleRaciMark(item, party) {
    const cycle = [null, "R", "A", "C", "I"];
    const cur = raciAssigns[item.RaciItemID]?.[party.RaciPartyID] || null;
    const next = cycle[(cycle.indexOf(cur) + 1) % cycle.length];
    const prev = raciAssigns;
    setRaciAssigns(m => {
      const row = { ...(m[item.RaciItemID] || {}) };
      if (next) row[party.RaciPartyID] = next;
      else delete row[party.RaciPartyID];
      return { ...m, [item.RaciItemID]: row };
    });
    try {
      const payload = JSON.stringify({
        turnover_id:   parseInt(turnoverID),
        raci_item_id:  item.RaciItemID,
        raci_party_id: party.RaciPartyID,
        mark:          next,
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_SaveTurnoverRaciMark @payload = '${payload}'`);
    } catch (e) {
      setRaciAssigns(prev); // roll back the optimistic update
      alert("Error saving mark: " + e.message);
    }
  }

  async function deleteRaciItem(item) {
    if (!window.confirm(`Remove "${item.Activity}" from the RACI matrix?`)) return;
    try {
      const payload = JSON.stringify({ raci_item_id: item.RaciItemID, turnover_id: parseInt(turnoverID), is_delete: 1 }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_SaveTurnoverRaciItem @payload = '${payload}'`);
      setRaciItems(prev => prev.filter(r => r.RaciItemID !== item.RaciItemID));
    } catch (e) { alert("Error: " + e.message); }
  }

  async function addCustomRaciItem() {
    if (!newRaciItem.activity.trim()) return;
    setAddingRaciItem(true);
    try {
      const payload = JSON.stringify({
        turnover_id:   parseInt(turnoverID),
        phase:         newRaciItem.phase,
        category:      newRaciItem.category.trim() || null,
        activity:      newRaciItem.activity.trim(),
        is_custom:     1,
        display_order: 9000, // custom rows sort after the seeded template within their phase
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_SaveTurnoverRaciItem @payload = '${payload}'`);
      const detail = await sqlRead(`EXEC sp_GetTurnoverDetail @turnover_id = ${parseInt(turnoverID)}`);
      const row = detail?.ResultSets?.Table1?.[0] || detail?.Table1?.[0] || detail?.value?.[0] || null;
      if (row?.RaciItemsJson) setRaciItems(JSON.parse(row.RaciItemsJson));
      setNewRaciItem({ phase: newRaciItem.phase, category: "", activity: "" });
    } catch (e) { alert("Error: " + e.message); }
    finally { setAddingRaciItem(false); }
  }

  async function seedRaciItems() {
    setSeedingRaci(true);
    try {
      await sqlWrite(`EXEC sp_SeedTurnoverRaciItems @turnover_id = ${parseInt(turnoverID)}`);
      const detail = await sqlRead(`EXEC sp_GetTurnoverDetail @turnover_id = ${parseInt(turnoverID)}`);
      const row = detail?.ResultSets?.Table1?.[0] || detail?.Table1?.[0] || detail?.value?.[0] || null;
      if (row?.RaciItemsJson)   setRaciItems(JSON.parse(row.RaciItemsJson));
      if (row?.RaciPartiesJson) setRaciParties(JSON.parse(row.RaciPartiesJson));
      setRaciAssigns(buildRaciAssignMap(row?.RaciAssignmentsJson ? JSON.parse(row.RaciAssignmentsJson) : []));
    } catch (e) { alert("Error loading RACI template: " + e.message); }
    finally { setSeedingRaci(false); }
  }

  async function renameRaciParty(party, name) {
    try {
      const payload = JSON.stringify({
        raci_party_id: party.RaciPartyID,
        turnover_id:   parseInt(turnoverID),
        party_name:    name,
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_SaveTurnoverRaciParty @payload = '${payload}'`);
      setRaciParties(prev => prev.map(p => p.RaciPartyID === party.RaciPartyID ? { ...p, PartyName: name } : p));
    } catch (e) { alert("Error saving party name: " + e.message); }
  }

  async function addRaciParty() {
    const name = window.prompt("Party name (firm or person):");
    if (!name?.trim()) return;
    setAddingRaciParty(true);
    try {
      const payload = JSON.stringify({
        turnover_id: parseInt(turnoverID),
        party_name:  name.trim(),
      }).replace(/'/g, "''");
      const res = await sqlWrite(`EXEC sp_SaveTurnoverRaciParty @payload = '${payload}'`);
      const newId = res?.ResultSets?.Table1?.[0]?.RaciPartyID || res?.Table1?.[0]?.RaciPartyID;
      if (newId) {
        setRaciParties(prev => [...prev, {
          RaciPartyID: newId, TurnoverID: parseInt(turnoverID),
          PartyName: name.trim(), PartyRole: null, IsCleanSpace: false,
          DisplayOrder: (prev[prev.length - 1]?.DisplayOrder || 0) + 10,
        }]);
      } else {
        load(); // fall back to a full refresh if the new ID didn't come back
      }
    } catch (e) { alert("Error adding party: " + e.message); }
    finally { setAddingRaciParty(false); }
  }

  async function deleteRaciParty(party) {
    if (!window.confirm(`Remove "${party.PartyName}" and all of its R/A/C/I marks?`)) return;
    try {
      const payload = JSON.stringify({
        raci_party_id: party.RaciPartyID,
        turnover_id:   parseInt(turnoverID),
        is_delete:     1,
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_SaveTurnoverRaciParty @payload = '${payload}'`);
      setRaciParties(prev => prev.filter(p => p.RaciPartyID !== party.RaciPartyID));
      setRaciAssigns(prev => {
        const next = {};
        for (const [itemId, marks] of Object.entries(prev)) {
          const { [party.RaciPartyID]: _removed, ...rest } = marks;
          next[itemId] = rest;
        }
        return next;
      });
    } catch (e) { alert("Error removing party: " + e.message); }
  }

  // Push the CleanSpace-responsible RACI rows to the estimator as an action item
  // (with the standard assignment notification email carrying the full item list),
  // then stamp SentToEstimating so the matrix shows what has been flagged.
  // Unused while RACI is in development (button hidden) — kept to re-enable.
  async function _sendRaciScopeToEstimator() {
    const scope = raciItems.filter(i => isCsScope(i, raciParties, raciAssigns));
    if (scope.length === 0) return;
    const csParty = raciParties.find(p => p.IsCleanSpace);
    const csMark = (item) => (csParty ? raciAssigns[item.RaciItemID]?.[csParty.RaciPartyID] : null);
    setSendingRaciScope(true);
    try {
      const assignedTo    = turnover?.Estimator       || null;
      const assignedEmail = turnover?.EstimatorEmail  || null;
      const description   = `Review RACI Matrix scope (Section 6) — confirm the estimate covers all ${scope.length} CleanSpace-responsible activities`;

      const actionPayload = JSON.stringify({
        turnover_id:    parseInt(turnoverID),
        description,
        assigned_to:    assignedTo,
        assigned_email: assignedEmail,
        created_by:     currentUser?.name || "",
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_SaveTurnoverActionItem @payload = '${actionPayload}'`);

      const markPayload = JSON.stringify({
        turnover_id: parseInt(turnoverID),
        item_ids:    scope.map(i => i.RaciItemID),
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_MarkTurnoverRaciSent @payload = '${markPayload}'`);

      // Notification email with the full scope list — best-effort, non-blocking.
      if (assignedEmail) {
        try {
          const mailToken = await getMailToken(instance, accounts, loginScopes);
          const platformUrl = `https://estimating.cleanspaceus.com/turnover/${turnoverID}`;
          const firstName = assignedTo?.split(" ")[0] || "there";
          const byPhase = {};
          scope.forEach(it => {
            if (!byPhase[it.Phase]) byPhase[it.Phase] = [];
            byPhase[it.Phase].push(it);
          });
          const phaseLabel = (ph) => {
            const def = RACI_PHASES.find(p => p.key === ph || (p.key === "Commissioning" && ph === "Startup & Commissioning"));
            return def ? `${def.num} — ${def.key}` : ph;
          };
          const listHtml = Object.entries(byPhase).map(([phase, arr]) => `
      <p style="margin:12px 0 4px;font-size:13px;font-weight:700;color:#1e3a5f;">${phaseLabel(phase)}</p>
      <ul style="margin:0;padding-left:18px;font-size:13px;color:#111;">
        ${arr.map(it => `<li style="margin-bottom:3px;">${it.Activity}${csMark(it) === "A" ? " <strong>(Accountable)</strong>" : ""}${it.Notes ? ` — <em style="color:#6b7280;">${it.Notes}</em>` : ""}</li>`).join("")}
      </ul>`).join("");
          const htmlBody = `
<div style="font-family:Arial,sans-serif;max-width:640px;color:#111;">
  <div style="background:#1e3a5f;padding:18px 24px;border-radius:8px 8px 0 0;">
    <div style="color:#fff;font-size:17px;font-weight:700;">RACI Scope for Estimating</div>
    <div style="color:#93c5fd;font-size:13px;margin-top:3px;">${turnover?.ProjectName || "EstAssist Turnover"}</div>
  </div>
  <div style="border:1px solid #e5e7eb;border-top:none;border-radius:0 0 8px 8px;padding:22px 24px;">
    <p style="margin:0 0 14px;font-size:14px;">Hi ${firstName},</p>
    <p style="margin:0 0 14px;font-size:14px;">The RACI Matrix on the <strong>${turnover?.ProjectName || "turnover"}</strong> package marks the following ${scope.length} activities as CleanSpace-responsible. Please confirm the estimate covers each of these scope items.</p>
    <div style="background:#f8f9fb;border:1px solid #e5e7eb;border-radius:6px;padding:14px 18px;margin-bottom:16px;">
      ${listHtml}
    </div>
    <div style="margin-bottom:16px;">
      <a href="${platformUrl}" style="display:inline-block;background:#1e3a5f;color:#fff;padding:9px 20px;border-radius:6px;font-size:13px;font-weight:700;text-decoration:none;">
        Open in Turnover Engine →
      </a>
    </div>
    <p style="margin:0;font-size:12px;color:#6b7280;">Sent by ${currentUser?.name || "EstAssist"} from the Section 6 RACI Matrix.</p>
  </div>
</div>`;
          await fetch(`${GRAPH_BASE}/me/sendMail`, {
            method: "POST",
            headers: { Authorization: `Bearer ${mailToken.accessToken}`, "Content-Type": "application/json" },
            body: JSON.stringify({
              message: {
                subject: `RACI Scope Review — ${scope.length} CleanSpace items (${turnover?.ProjectName})`,
                body: { contentType: "HTML", content: htmlBody },
                toRecipients: [{ emailAddress: { address: assignedEmail, name: assignedTo || assignedEmail } }],
              },
              saveToSentItems: true,
            }),
          });
        } catch (emailErr) {
          console.warn("RACI scope notification email failed (non-blocking):", emailErr.message);
        }
      }

      load(); // refresh action items + SentToEstimating flags
    } catch (e) {
      alert("Error sending scope to estimating: " + e.message);
    } finally {
      setSendingRaciScope(false);
    }
  }

  // ── Cover sheet save ─────────────────────────────────────────────────────────
  async function saveCoverSheet() {
    if (!coverDirty) return;
    setSavingCover(true);
    try {
      const payload = JSON.stringify({
        turnover_id:          parseInt(turnoverID),
        project_manager:      coverForm.project_manager      || null,
        planned_turnover_date:coverForm.planned_turnover_date || null,
        commission_splits:    coverForm.commission_splits     || [],
        ...(hasMarketingKickoff(turnover) ? {
          public_reference:         coverForm.public_reference         || "",
          confidentiality_notes:    coverForm.confidentiality_notes    || "",
          photography_restrictions: coverForm.photography_restrictions || "",
        } : {}),
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_UpdateTurnoverCoverSheet @payload = '${payload}'`);
      setCoverDirty(false);
      // Auto-complete Section 1 when all required fields are filled — no manual click needed
      const sec1 = sections.find(s => s.SectionNumber === 1);
      if (sec1 && !sec1.IsComplete && getCoverSheetMissing().length === 0) {
        const secPayload = JSON.stringify({
          section_id:  sec1.SectionID,
          turnover_id: parseInt(turnoverID),
          is_complete: 1,
        }).replace(/'/g, "''");
        await sqlWrite(`EXEC sp_UpdateTurnoverSection @payload = '${secPayload}'`);
      }
      load();
    } catch (e) {
      alert("Error saving cover sheet: " + e.message);
    } finally {
      setSavingCover(false);
    }
  }

  // ── Cover Sheet completion ─────────────────────────────────────────────────
  // Section 1 has no document uploads — "complete" means the required fields
  // are filled. Validation lists exactly what's missing (pilot feedback:
  // never block completion without saying why).
  function getCoverSheetMissing() {
    const missing = [];
    if (!(coverForm.project_manager || turnover?.ProjectManager)) missing.push("Project Manager");
    if (!(coverForm.planned_turnover_date || turnover?.PlannedTurnoverDate)) missing.push("Planned Turnover Date");
    if (hasMarketingKickoff(turnover) && !(coverForm.public_reference || turnover?.PublicReference)) missing.push("Client confidentiality — can the project be publicly referenced?");
    const commissionProblem = commissionIssue(coverForm.commission_splits);
    if (commissionProblem) missing.push(commissionProblem);
    return missing;
  }

  async function toggleCoverSheetComplete() {
    const sec1 = sections.find(s => s.SectionNumber === 1);
    if (!sec1) return;
    if (!isAssignedOwner(sec1)) {
      alert(`Only ${sec1.AssignedTo || "the assigned owner"} can mark this section complete.`);
      return;
    }
    // Un-completing needs no validation
    if (!sec1.IsComplete) {
      const missing = getCoverSheetMissing();
      if (missing.length > 0) {
        setCompletionBlockers({ sectionId: sec1.SectionID, missing });
        return;
      }
      if (coverDirty) await saveCoverSheet();
    }
    setCompletionBlockers(null);
    try {
      const payload = JSON.stringify({
        section_id:  sec1.SectionID,
        turnover_id: parseInt(turnoverID),
        is_complete: sec1.IsComplete ? 0 : 1,
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_UpdateTurnoverSection @payload = '${payload}'`);
      load();
    } catch (e) {
      alert("Error: " + e.message);
    }
  }

  function setCover(field, val) {
    setCoverForm(f => ({ ...f, [field]: val }));
    setCoverDirty(true);
  }

  function addCommissionRow() {
    setCoverForm(f => ({
      ...f,
      commission_splits: [
        ...(f.commission_splits || []),
        { id: Date.now() + Math.random(), name: "", role: "Sales", split_pct: "" },
      ],
    }));
    setCoverDirty(true);
  }
  function updateCommissionRow(id, field, val) {
    setCoverForm(f => ({
      ...f,
      commission_splits: (f.commission_splits || []).map(r => r.id === id ? { ...r, [field]: val } : r),
    }));
    setCoverDirty(true);
  }
  // "No commission on this job": one marker row replaces the contributor rows;
  // turning it off puts back the salesperson row to start from.
  function setNoCommission(on) {
    setCoverForm(f => ({
      ...f,
      commission_splits: on
        ? noCommissionSplits()
        : [{ id: Date.now() + Math.random(), name: turnover?.Salesperson || "", role: "Sales", split_pct: "" }],
    }));
    setCoverDirty(true);
  }
  function removeCommissionRow(id) {
    setCoverForm(f => ({
      ...f,
      commission_splits: (f.commission_splits || []).filter(r => r.id !== id),
    }));
    setCoverDirty(true);
  }

  function goToSection(num) {
    const sec = sections.find(s => s.SectionNumber === num);
    if (sec) { setShowSummaryFlyout(false); selectSection(sec); }
  }

  // ── File upload ──────────────────────────────────────────────────────────────
  async function handleFileUpload(file) {
    if (!graphToken) {
      alert("SharePoint not connected. Please refresh and try again.");
      return;
    }
    setUploading(true);
    try {
      const result = await resolveSpUpload(file, { turnover, graphToken, estDriveInfo, driveInfo, activeSection, getJobFolder });

      // Save link back to SQL
      const payload = JSON.stringify({
        section_id:    activeSection.SectionID,
        is_complete:   true,
        sharepoint_url: result.webUrl,
        file_name:     result.name,
        uploaded_by:   currentUser?.name || "",
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_UpdateTurnoverSection @payload = '${payload}'`);
      load();
    } catch (e) {
      alert("Upload failed: " + e.message);
    } finally {
      setUploading(false);
    }
  }

  // ── Save notes ───────────────────────────────────────────────────────────────
  async function saveNotes() {
    if (!activeSection) return;
    setSavingNotes(true);
    try {
      const payload = JSON.stringify({
        section_id: activeSection.SectionID,
        notes:      notesText,
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_UpdateTurnoverSection @payload = '${payload}'`);
      setNotesDirty(false);
      load();
    } catch (e) {
      alert("Error saving notes: " + e.message);
    } finally {
      setSavingNotes(false);
    }
  }

  // ── Save section 16 permits/bonds/LD terms ────────────────────────────────────
  // Serializes the structured terms object as JSON into TurnoverSections.Notes
  // for section 16. The Notes field is repurposed as a JSON store for this
  // section only; all other sections keep using it as plain text.
  // Layout-2 §4 Contract Requirements — one checklist row per save
  // (sp_SaveContractRequirement writes only the keys sent). Optimistic update.
  async function saveContractRequirement(key, patch) {
    setContractRequirements(prev => {
      const has = prev.some(r => r.RequirementKey === key);
      const next = { RequirementKey: key, ...(prev.find(r => r.RequirementKey === key) || {}) };
      if ("applies" in patch) next.Applies = patch.applies;
      if ("responsibility" in patch) next.Responsibility = patch.responsibility;
      if ("details" in patch) next.DetailsJson = JSON.stringify(patch.details || {});
      return has ? prev.map(r => (r.RequirementKey === key ? next : r)) : [...prev, next];
    });
    setSavingRequirement(true);
    try {
      const body = JSON.stringify(requirementPayload(parseInt(turnoverID), key, patch, currentUser)).replace(/'/g, "''");
      const res = await sqlWrite(`EXEC sp_SaveContractRequirement @payload = '${body}'`);
      const row = res?.ResultSets?.Table1?.[0] || res?.Table1?.[0];
      if (!row || row.Result !== 1) { alert(row?.Error || "The requirement could not be saved."); load(); }
    } catch (e) {
      alert("Error saving the requirement: " + e.message);
      load();
    } finally {
      setSavingRequirement(false);
    }
  }

  async function savePermitsTerms(updatedTerms) {
    const permitsNum = sectionNumberOf(turnover, "permits");
    const sec16 = permitsNum ? sections.find(s => s.SectionNumber === permitsNum) : null;
    if (!sec16) return;
    setSavingPermits(true);
    try {
      const payload = JSON.stringify({
        section_id: sec16.SectionID,
        notes:      JSON.stringify(updatedTerms),
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_UpdateTurnoverSection @payload = '${payload}'`);
    } catch (e) {
      alert("Error saving permits terms: " + e.message);
    } finally {
      setSavingPermits(false);
    }
  }

  async function saveFinancialCompliance(updated) {
    setFinancialCompliance(updated);
    setSavingFinancialCompliance(true);
    try {
      const payload = { turnover_id: turnoverID };
      for (const f of [...FINANCIAL_COMPLIANCE_FIELDS, ...TURNOVER_ONLY_FIELDS]) {
        payload[f.param] = updated[f.key] ?? null;
      }
      const esc = JSON.stringify(payload).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_SaveTurnoverFinancialCompliance @payload = '${esc}'`);
    } catch (e) {
      alert("Error saving financial compliance: " + e.message);
    } finally {
      setSavingFinancialCompliance(false);
    }
  }

  // ── Toggle N/A ────────────────────────────────────────────────────────────────
  async function toggleNA() {
    if (!activeSection) return;
    if (!isAssignedOwner(activeSection)) {
      alert(`Only ${activeSection.AssignedTo || "the assigned owner"} can change this section's status.`);
      return;
    }
    const newApplicable = !activeSection.IsApplicable;
    try {
      const payload = JSON.stringify({
        section_id:    activeSection.SectionID,
        is_applicable: newApplicable,
        is_complete:   newApplicable ? activeSection.IsComplete : false,
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_UpdateTurnoverSection @payload = '${payload}'`);
      load();
    } catch (e) {
      alert("Error: " + e.message);
    }
  }

  // ── Mark complete manually (no file) ─────────────────────────────────────────
  // Un-completing needs no validation. Completing does: instead of a disabled
  // button or an alert(), a missing checklist blocks the toggle and surfaces
  // as an inline "Cannot Complete Section" panel right in the section header
  // (see completionBlockers / getSectionMissing above).
  async function toggleComplete() {
    if (!activeSection) return;
    if (!isAssignedOwner(activeSection)) {
      alert(`Only ${activeSection.AssignedTo || "the assigned owner"} can mark this section complete.`);
      return;
    }
    if (!activeSection.IsComplete) {
      const missing = getSectionMissing(activeSection);
      if (missing.length > 0) {
        setCompletionBlockers({ sectionId: activeSection.SectionID, missing });
        return;
      }
    }
    setCompletionBlockers(null);
    try {
      const payload = JSON.stringify({
        section_id:  activeSection.SectionID,
        is_complete: !activeSection.IsComplete,
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_UpdateTurnoverSection @payload = '${payload}'`);
      load();
    } catch (e) {
      alert("Error: " + e.message);
    }
  }

  // ── Reassign a section's owner (sets AssignmentSource to 'override') ────────
  async function reassignSection(sectionId, name, email) {
    try {
      const payload = JSON.stringify({
        section_id:      sectionId,
        assigned_to:     name || "",
        assigned_email:  email || "",
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_UpdateTurnoverSection @payload = '${payload}'`);
      load();
    } catch (e) {
      alert("Error: " + e.message);
    }
  }

  // ── Reset a section back to its role default (Salesperson/Estimator) ────────
  async function resetSectionAssignment(sectionId) {
    try {
      const payload = JSON.stringify({
        section_id:        sectionId,
        reset_to_default:  true,
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_UpdateTurnoverSection @payload = '${payload}'`);
      load();
    } catch (e) {
      alert("Error: " + e.message);
    }
  }

  // ── Add action item ───────────────────────────────────────────────────────────
  async function addActionItem() {
    if (!newAction.description.trim()) return;
    setAddingAction(true);
    try {
      const payload = JSON.stringify({
        turnover_id:    parseInt(turnoverID),
        description:    newAction.description,
        assigned_to:    newAction.assigned_to    || null,
        assigned_email: newAction.assigned_email || null,
        due_date:       newAction.due_date       || null,
        created_by:     currentUser?.name        || "",
      }).replace(/'/g, "''");
      await sqlWrite(`EXEC sp_SaveTurnoverActionItem @payload = '${payload}'`);
      setNewAction({ description: "", assigned_to: "", assigned_email: "", due_date: "" });
      load();

      // Send email notification to the assignee if they have an email address.
      // Best-effort — failure doesn't block the action item from being saved.
      if (newAction.assigned_email?.trim()) {
        try {
          const mailToken = await getMailToken(instance, accounts, loginScopes);
          const platformUrl = `https://estimating.cleanspaceus.com/turnover/${turnoverID}`;
          const firstName = newAction.assigned_to?.split(" ")[0] || "there";
          const dueLine = newAction.due_date
            ? `<p style="margin:0 0 12px; font-size:13px;"><strong>Due date:</strong> ${new Date(newAction.due_date + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/New_York", month: "long", day: "numeric", year: "numeric" })}</p>`
            : "";
          const htmlBody = `
<div style="font-family:Arial,sans-serif;max-width:640px;color:#111;">
  <div style="background:#1e3a5f;padding:18px 24px;border-radius:8px 8px 0 0;">
    <div style="color:#fff;font-size:17px;font-weight:700;">Action Item Assigned</div>
    <div style="color:#93c5fd;font-size:13px;margin-top:3px;">${turnover?.ProjectName || "EstAssist Turnover"}</div>
  </div>
  <div style="border:1px solid #e5e7eb;border-top:none;border-radius:0 0 8px 8px;padding:22px 24px;">
    <p style="margin:0 0 14px;font-size:14px;">Hi ${firstName},</p>
    <p style="margin:0 0 14px;font-size:14px;">You've been assigned an action item on the <strong>${turnover?.ProjectName || "turnover"}</strong> package.</p>
    <div style="background:#f8f9fb;border:1px solid #e5e7eb;border-radius:6px;padding:14px 18px;margin-bottom:16px;">
      <p style="margin:0 0 8px;font-size:14px;font-weight:600;">${newAction.description}</p>
      ${dueLine}
      <p style="margin:0;font-size:12px;color:#6b7280;">Assigned by ${currentUser?.name || "EstAssist"}</p>
    </div>
    <div style="margin-bottom:16px;">
      <a href="${platformUrl}" style="display:inline-block;background:#1e3a5f;color:#fff;padding:9px 20px;border-radius:6px;font-size:13px;font-weight:700;text-decoration:none;">
        Open in Turnover Engine →
      </a>
    </div>
    <p style="margin:0;font-size:12px;color:#6b7280;">Reply to this email if you have questions about this action item.</p>
  </div>
</div>`;
          await fetch(`${GRAPH_BASE}/me/sendMail`, {
            method: "POST",
            headers: { Authorization: `Bearer ${mailToken.accessToken}`, "Content-Type": "application/json" },
            body: JSON.stringify({
              message: {
                subject: `Action Item — ${newAction.description.substring(0, 60)}${newAction.description.length > 60 ? "…" : ""} (${turnover?.ProjectName})`,
                body: { contentType: "HTML", content: htmlBody },
                toRecipients: [{ emailAddress: { address: newAction.assigned_email, name: newAction.assigned_to || newAction.assigned_email } }],
              },
              saveToSentItems: true,
            }),
          });
        } catch (emailErr) {
          console.warn("Action item notification email failed (non-blocking):", emailErr.message);
          // Don't alert — the action item was saved successfully; email is a bonus
        }
      }
    } catch (e) {
      alert("Error: " + e.message);
    } finally {
      setAddingAction(false);
    }
  }

  // ── Derived values ────────────────────────────────────────────────────────────
  const completePct = turnover?.CompletionPct || 0;
  const applicable  = sections.filter(s => s.IsApplicable);
  const complete    = sections.filter(s => s.IsComplete);
  const openActions = actionItems.filter(a => !a.IsComplete);

  // Re-sync activeSection when sections reload
  useEffect(() => {
    if (activeSection && sections.length > 0) {
      const refreshed = sections.find(s => s.SectionID === activeSection.SectionID);
      if (refreshed) setActiveSection(refreshed);
    }
  }, [sections]); // eslint-disable-line

  // Clear the "cannot complete" panel when the user navigates to a different section
  useEffect(() => {
    setCompletionBlockers(null);
  }, [activeSection?.SectionID]);

  // ── Loading / error ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>
        <div className="spinner spinner-dark" style={{ display: "inline-block", marginRight: 8 }} />
        Loading turnover…
      </div>
    );
  }
  if (error || !turnover) {
    return <div style={{ padding: 24, color: "var(--red)" }}>{error || "Turnover not found"}</div>;
  }

  // Cover Sheet (1) is always unlocked — it's just data entry, no status gate needed.
  // Sections 2 and 3 unlock during Contract Review; all others need Active+.
  const CONTRACT_REVIEW_SECTIONS = new Set([1, 2, 3]);
  const statusUnlocked = turnover.Status === "Contract Review"
    ? CONTRACT_REVIEW_SECTIONS.has(activeSection?.SectionNumber || 0)
    : turnover.Status !== "Pending Number" && turnover.Status !== "Numbered";
  const isSectionOwner = isAssignedOwner(activeSection);
  const canUpload = statusUnlocked && isSectionOwner;
  const uploadLockMessage = !statusUnlocked
    ? "🔒 Uploads unlock once Accounting assigns a job number."
    : !isSectionOwner
      ? `🔒 Only ${activeSection?.AssignedTo || "the assigned owner"}${sharesWithSalesperson(turnover, activeSection?.SectionNumber) && turnover?.Salesperson ? ` or ${turnover.Salesperson}` : ""} can upload documents to this section.`
      : "";

  // ── Workflow engine ───────────────────────────────────────────────────────────
  // Everything below turns the scattered signals (status, section completion,
  // contract review sign-offs, document counts) into ONE deterministic answer:
  // what is the single next thing standing in the way of this turnover moving
  // forward, and why. Nothing here changes what's stored — it's a read-only
  // lens over data that's already loaded.

  // Is a given section unlocked for work under the turnover's current status?
  // Generalizes the activeSection-specific `statusUnlocked` above to any section.
  function sectionStatusUnlocked(sec) {
    return turnover.Status === "Contract Review"
      ? CONTRACT_REVIEW_SECTIONS.has(sec?.SectionNumber || 0)
      : turnover.Status !== "Pending Number" && turnover.Status !== "Numbered";
  }

  // What, specifically, is missing before a section can be considered done.
  // Returns an array of human-readable strings — empty means nothing's missing.
  function getSectionMissing(section) {
    if (!section || !section.IsApplicable) return [];
    if (section.SectionNumber === 1) return getCoverSheetMissing();
    if (section.SectionNumber === CONTRACT_REVIEW_MATRIX_SECTION) {
      return contractGate({ reviews: contractReview, issues: contractIssues, turnover }).blockers.map(b => b.text);
    }
    // Section 3 completes when the signed contract is recorded (sp_MarkContractExecuted).
    // Only in Contract Review: a turnover past it predates this step and can't
    // record it any more, so it keeps the ordinary document-or-note rule.
    // A job number requested through the administrator exception keeps §3 open
    // until the signed contract is recorded, whatever the status now is.
    if (section.SectionNumber === 3 && exceptionOutstanding(turnover)) {
      return ["Executed contract still to come — upload it (Type: Executed Contract) and mark it executed"];
    }
    if (section.SectionNumber === 3 && turnover?.Status === "Contract Review" && !turnover?.ContractExecutedDate) {
      return ["Mark the contract executed (signed date, signer and the uploaded Executed Contract)"];
    }
    // Section 7 — every Estimate must tie to a HubSpot release, and every
    // released line item needs one. A note never substitutes, and an upload
    // doesn't auto-complete this section (sp_SaveTurnoverDocument).
    if (section.SectionNumber === ESTIMATE_SUMMARY_SECTION) {
      return checkEstimateReleases(hsLineItems, documents.filter(d => d.SectionID === section.SectionID)).missing;
    }
    // A document upload auto-completes the section on its own (see
    // handleFileUpload), so Mark Complete is only actually needed when
    // there's no file to upload — a note on the section counts too.
    const hasDoc   = !!section.SharePointUrl || documents.some(d => d.SectionID === section.SectionID);
    const hasNotes = !!(section.Notes && section.Notes.trim());
    const missing = (hasDoc || hasNotes) ? [] : ["Upload a document or add a note"];
    // Section 4 — Prevailing Wage flag (Financial Compliance) requires the
    // Prevailing Wage Determination doc before the section counts as done.
    if (sectionKind(turnover, section.SectionNumber) === "marketing") {
      return kickoffMissing(marketingKickoff, { schedule, turnover });
    }
    if (sectionKind(turnover, section.SectionNumber) === "requirements") {
      return evaluateRequirements(contractRequirements, documents.filter(d => d.SectionID === section.SectionID)).missing;
    }
    return missing;
  }

  // Traffic-light health for a section: complete (🟢) / needs attention (🟡,
  // unlocked but missing something) / blocked (🔴, not yet unlocked by status).
  function getSectionHealth(section) {
    if (!section) return { level: "na", label: "", reason: "" };
    if (!section.IsApplicable) return { level: "na", label: "Not Applicable", reason: "" };
    if (section.IsComplete) {
      if (section.SectionNumber === 1 && !turnover?.JobNumber) {
        return { level: "ready", label: "Awaiting job number", reason: "Turns green once a job number is assigned" };
      }
      // Completed before the release check existed (or a release changed in
      // HubSpot since): flag it rather than silently showing green.
      if (section.SectionNumber === ESTIMATE_SUMMARY_SECTION) {
        const missing = getSectionMissing(section);
        if (missing.length > 0) {
          return { level: "attention", label: "Estimate ≠ release", reason: missing[0] + (missing.length > 1 ? ` (+${missing.length - 1} more)` : "") };
        }
      }
      return { level: "complete", label: "Complete", reason: "" };
    }
    if (!sectionStatusUnlocked(section)) {
      return { level: "blocked", label: "Blocked", reason: "Not unlocked yet at the turnover's current stage" };
    }
    const missing = getSectionMissing(section);
    if (missing.length === 0) return { level: "ready", label: "Ready to complete", reason: "" };
    return { level: "attention", label: "Needs Attention", reason: missing[0] + (missing.length > 1 ? ` (+${missing.length - 1} more)` : "") };
  }

  // The single dominant "what happens next" answer for the whole turnover —
  // one owner, one action, one reason, one place to click. This is what
  // replaces the old multi-line "Next Action Banner" that tried to summarize
  // everything remaining at once.
  function getNextRequiredAction() {
    const st = turnover.Status;

    if (st === "Turned Over") {
      return { stage: st, owner: null, action: "Turnover complete", reason: "No further action needed.", targetSection: null, isDone: true };
    }

    // A section already marked complete that now fails its data check
    // (only §7's release reconciliation today) outranks the normal next step.
    if (st !== "Contract Review" && st !== "Pending Number") {
      const est = sections.find(s => s.SectionNumber === ESTIMATE_SUMMARY_SECTION);
      if (est?.IsApplicable && est.IsComplete) {
        const missing = getSectionMissing(est);
        if (missing.length > 0) {
          return {
            stage: st, owner: est.AssignedTo || turnover.Estimator || "Estimator",
            action: `Fix §${ESTIMATE_SUMMARY_SECTION} — estimate doesn't match the HubSpot releases`,
            reason: missing[0] + (missing.length > 1 ? ` (+${missing.length - 1} more)` : ""),
            targetSection: ESTIMATE_SUMMARY_SECTION,
          };
        }
      }
    }

    // Sales owns the handoff: sends the package and schedules the turnover
    // meeting (Email Package can send the invite); the PM attends and receives.
    if (st === "Package Complete") {
      const pm = turnover.ProjectManager ? ` with ${turnover.ProjectManager}` : " with the Project Manager";
      return {
        stage: st, owner: turnover.Salesperson || "Salesperson",
        action: "Send the package and schedule the turnover meeting",
        reason: `All ${applicable.length} applicable sections are complete. Print Package, then Email Package to send it and invite the team${pm}.`
          + (hasMarketingKickoff(turnover) ? " Marketing is invited too — the turnover meeting covers the Marketing section." : ""),
        targetSection: null, ctaLabel: "🖨 Print Package", ctaAction: "print",
      };
    }

    if (st === "Contract Review") {
      const section3 = sections.find(s => s.SectionNumber === 3);
      const gate = contractGate({ reviews: contractReview, issues: contractIssues, turnover, documents, section3 });
      const list = (xs, name) => {
        const names = xs.slice(0, 3).map(name).join(", ");
        return xs.length > 3 ? `${names} +${xs.length - 3} more` : names;
      };
      if (gate.stage === "signoff") {
        const waiting = contractReview.filter(r => r.Status === "Pending" || (r.Status === "Rejected" && !contractIssues.some(i => i.ReviewID === r.ReviewID && (i.MustResolve === true || i.MustResolve === 1))));
        return {
          stage: st, owner: waiting[0]?.AssignedTo || waiting[0]?.Department,
          action: "Get department sign-off", reason: `Waiting on: ${list(waiting, r => r.Department)}`,
          targetSection: CONTRACT_REVIEW_MATRIX_SECTION,
        };
      }
      if (gate.stage === "resolve") {
        return {
          stage: st, owner: turnover.Salesperson,
          action: `Resolve ${gate.toResolve.length} contract issue${gate.toResolve.length === 1 ? "" : "s"} with the issuer`,
          reason: "Record the agreed outcome on each must-resolve issue under Contract Issues.",
          targetSection: CONTRACT_REVIEW_MATRIX_SECTION,
        };
      }
      if (gate.stage === "accept") {
        const depts = [...new Set(gate.toAccept.map(i => contractReview.find(r => r.ReviewID === i.ReviewID)?.Department).filter(Boolean))];
        const firstRow = contractReview.find(r => r.ReviewID === gate.toAccept[0].ReviewID);
        return {
          stage: st, owner: firstRow?.AssignedTo || firstRow?.Department,
          action: "Accept the negotiated contract outcomes", reason: `Waiting on: ${list(depts, d => d)}`,
          targetSection: CONTRACT_REVIEW_MATRIX_SECTION,
        };
      }
      if (gate.stage === "rejected") {
        return {
          stage: st, owner: gate.rejected[0].AssignedTo || gate.rejected[0].Department,
          action: "Update the department status", reason: `Outcomes accepted — change ${list(gate.rejected, r => r.Department)} from Rejected.`,
          targetSection: CONTRACT_REVIEW_MATRIX_SECTION,
        };
      }
      if (gate.stage === "execute") {
        return {
          stage: st, owner: turnover.Salesperson,
          action: "Sign the contract and mark it executed",
          reason: gate.executedDocs.length
            ? "Record the signed date and signer on Section 3."
            : "Upload the signed contract to Section 3 (Type: Executed Contract), then mark it executed.",
          targetSection: 3,
        };
      }
      return {
        stage: st, owner: turnover.Salesperson,
        action: "Submit for Job Number", reason: "Contract review is resolved and the executed contract is recorded.",
        targetSection: 3, ctaLabel: "✓ Submit for Job Number", ctaAction: "submit_job_number",
      };
    }

    if (st === "Pending Number") {
      return {
        stage: st, owner: "Nicole Markley",
        action: "Assign job number", reason: "24-hour target from request date.",
        targetSection: null,
      };
    }

    if (st === "Numbered") {
      const first = applicable.filter(s => !s.IsComplete).sort((a, b) => a.SectionNumber - b.SectionNumber)[0];
      return {
        stage: st, owner: turnover.Estimator,
        action: "Begin uploading documents", reason: first ? `Start with §${first.SectionNumber} ${first.SectionName}` : "",
        targetSection: first ? first.SectionNumber : null,
      };
    }

    if (st === "Active" || st === "In Progress") {
      const incomplete = applicable.filter(s => !s.IsComplete).sort((a, b) => a.SectionNumber - b.SectionNumber);
      if (incomplete.length === 0) {
        return {
          stage: st, owner: turnover.Estimator,
          action: "Mark package complete", reason: "All applicable sections are done.",
          targetSection: null,
        };
      }
      const next = incomplete[0];
      const missing = getSectionMissing(next);
      // Sections 1 (Cover Sheet) and the Contract Review Matrix don't have a
      // Mark N/A button — everywhere else, surface it as a valid way forward
      // right alongside the upload/note requirement.
      const canMarkNA = next.SectionNumber !== 1 && next.SectionNumber !== CONTRACT_REVIEW_MATRIX_SECTION;
      const reason = missing.length > 0
        ? `Missing: ${missing[0]}${missing.length > 1 ? ` (+${missing.length - 1} more)` : ""}${canMarkNA ? " — or click Mark N/A if it doesn't apply" : ""}`
        : "Ready — open the section to mark it complete.";
      return {
        stage: st, owner: next.AssignedTo || "Unassigned",
        action: `Complete §${next.SectionNumber} ${next.SectionName}`, reason,
        targetSection: next.SectionNumber,
      };
    }

    return null;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: `calc(100vh - ${navHeight}px)` }}>
      {/* Date input font fix is now global in index.css. */}
      {showSummaryFlyout && activeSection && (() => {
        const section2 = sections.find(s => s.SectionNumber === 2);
        const sectionSummaries = aiSummaries.filter(s => s.SectionID === activeSection.SectionID);
        const isCorrespondence = activeSection.SectionNumber === CORRESPONDENCE_SECTION;
        const typePrefixBase = isCorrespondence ? CORRESPONDENCE_PROMPT_CATEGORY : "contract_review";
        if (showSummaryFlyout === `combined_${typePrefixBase}` || showSummaryFlyout === "combined") {
          if (isCorrespondence) {
            return (
              <CorrespondenceTimelineFlyout
                documents={documents.filter(d => d.SectionID === activeSection.SectionID)}
                aiSummaries={sectionSummaries}
                typePrefixBase={typePrefixBase}
                projectName={turnover.ProjectName}
                onClose={() => setShowSummaryFlyout(null)}
              />
            );
          }
          return (
            <AISummaryFlyout
              summaries={sectionSummaries}
              summaryType={`${typePrefixBase}_combined`}
              title="Combined Contract Review"
              projectName={turnover.ProjectName}
              onClose={() => setShowSummaryFlyout(null)}
            />
          );
        }
        // Per-doc flyout — handle legacy format (contract_review_s*) and new per-doc format
        const doc = documents.find(d => d.TurnoverDocumentID === showSummaryFlyout);
        const summaryType = showSummaryFlyout === "legacy"
          ? "contract_review"
          : `${typePrefixBase}_doc_${showSummaryFlyout}`;
        const title = showSummaryFlyout === "legacy"
          ? (documents.find(d => d.SectionID === section2?.SectionID)?.FileName || "Document Review")
          : (doc?.FileName || "Document Review");
        return (
          <AISummaryFlyout
            summaries={sectionSummaries}
            summaryType={summaryType}
            title={title}
            projectName={turnover.ProjectName}
            onClose={() => setShowSummaryFlyout(null)}
          />
        );
      })()}
      <div style={{
        padding: isMobile ? "10px 14px 10px" : "14px 24px 12px", borderBottom: "1px solid var(--cs-border)",
        background: "var(--surface)", flexShrink: 0,
      }}>
        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 10 }}>
          <button onClick={() => navigate("/turnover")} style={{
            background: "none", border: "none", cursor: "pointer",
            fontSize: 12, color: "var(--cs-blue)", padding: "2px 4px",
          }}>Turnovers</button>
          <span style={{ color: "var(--cs-border)", fontSize: 13 }}>/</span>
          <span style={{
            fontSize: 12, fontWeight: 600, color: "var(--text)", padding: "2px 4px",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {turnover.JobNumber ? `${turnover.JobNumber} — ` : ""}{turnover.ProjectName}
          </span>
        </div>

        {isMobile ? (
          <>
            {/* Compact summary row */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text)", lineHeight: 1.25 }}>
                  {turnover.ProjectName}
                </div>
                {turnover.ClientName && (
                  <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 2 }}>{turnover.ClientName}</div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                <StatusPill status={turnover.Status} />
                {turnover.JobNumber && (
                  <div style={{ fontFamily: "monospace", fontWeight: 800, fontSize: 15, color: "var(--cs-blue)" }}>
                    {turnover.JobNumber}
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={() => setMobileDetailsOpen(o => !o)}
              style={{
                marginTop: 8, background: "none", border: "none", cursor: "pointer",
                fontSize: 11, color: "var(--cs-blue)", fontWeight: 600, padding: 0,
                display: "flex", alignItems: "center", gap: 4,
              }}
            >
              {mobileDetailsOpen ? "Hide details ▲" : "Show details ▼"}
              <span style={{ color: "var(--text3)", fontWeight: 400 }}>
                · {complete.length} of {applicable.length} sections complete
              </span>
            </button>

            {mobileDetailsOpen && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px", marginTop: 10 }}>
                <KV label="Salesperson" value={turnover.Salesperson} />
                <KV label="Project Manager" value={turnover.ProjectManager} />
                <KV label="Planned Turnover Date" value={fmtDate(turnover.PlannedTurnoverDate)} />
                <KV label="Market" value={turnover.Market} />
                <KV label="Type" value={turnover.ProjectType} />
                <KV label="Amount" value={(() => {
                  if (hsLineItems.length > 0) {
                    const total = hsLineItems.reduce((s, li) => s + (parseFloat(li.amount) || 0), 0);
                    return "$" + total.toLocaleString("en-US", { maximumFractionDigits: 0 });
                  }
                  return fmtCurrency(turnover.DealAmount);
                })()} />
                <KV label="Contract" value={turnover.ContractStructure} />
                <KV label="Labor" value={turnover.LaborStrategy} />
                <KV label="SF" value={turnover.SquareFeet ? `${parseInt(turnover.SquareFeet).toLocaleString()} SF` : null} />
              </div>
            )}
          </>
        ) : (
        /* Cover info grid */
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr auto", gap: "8px 20px", alignItems: "start" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text)", lineHeight: 1.2 }}>
              {turnover.ProjectName}
            </div>
            {turnover.ClientName && (
              <div style={{ fontSize: 13, color: "var(--text2)", marginTop: 2 }}>{turnover.ClientName}</div>
            )}
            {turnover.CityState && (
              <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 1 }}>📍 {turnover.CityState}</div>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <KV label="Salesperson" value={turnover.Salesperson} />
            <KV label="Project Manager" value={turnover.ProjectManager} />
            <KV label="Planned Turnover Date" value={fmtDate(turnover.PlannedTurnoverDate)} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <KV label="Market" value={turnover.Market} />
            <KV label="Type" value={turnover.ProjectType} />
            <KV label="Amount" value={(() => {
              if (hsLineItems.length > 0) {
                const total = hsLineItems.reduce((s, li) => s + (parseFloat(li.amount) || 0), 0);
                return "$" + total.toLocaleString("en-US", { maximumFractionDigits: 0 });
              }
              return fmtCurrency(turnover.DealAmount);
            })()} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <KV label="Contract" value={turnover.ContractStructure} />
            <KV label="Labor" value={turnover.LaborStrategy} />
            <KV label="SF" value={turnover.SquareFeet ? `${parseInt(turnover.SquareFeet).toLocaleString()} SF` : null} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
            <StatusPill status={turnover.Status} />
            {turnover.JobNumber && (
              <div style={{
                fontFamily: "monospace", fontWeight: 800, fontSize: 20,
                color: "var(--cs-blue)", letterSpacing: 1,
              }}>
                {turnover.JobNumber}
              </div>
            )}
            {/* Progress ring summary */}
            <div style={{ fontSize: 11, color: "var(--text3)" }}>
              {complete.length} of {applicable.length} sections complete
            </div>
          </div>
        </div>
        )}

        {/* Job folder — once the turnover has a job number: its SharePoint path
            (…› Turnover) and a link. The saved folder link when there is one,
            else the path uploads use (turnover/folderPath.js). */}
        {(() => {
          const loc = turnoverFolderLocation(turnover);
          if (!loc) return null;
          // Change folder: the Cover Sheet owner, Accounting (Job Number Assignment Alert) and admins.
          const me = String(currentUser?.email || "").toLowerCase();
          const sec1 = sections.find(x => x.SectionNumber === 1);
          const canChangeFolder = isAdmin || (sec1 && sec1.AssignedEmail && isAssignedOwner(sec1))
            || turnoverConfigContacts.some(c => c.GroupKey === "job_number_alert" && String(c.Email || "").toLowerCase() === me && me);
          const path = loc.parts.join(" › ");
          const pendingDocs = docsInEstimatingSite(documents);
          return (
            <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8, padding: "6px 10px", background: "#f8f9fb", border: "1px solid var(--cs-border)", borderRadius: 6, fontSize: 12, minWidth: 0 }}>
              <span style={{ fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", fontSize: 10, letterSpacing: "0.05em", whiteSpace: "nowrap" }}>📁 Turnover folder</span>
              <span title={path || loc.url} style={{ flex: 1, minWidth: 0, color: "var(--text2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {path || loc.url}
              </span>
              {path && (
                <button type="button" title="Copy the folder path"
                  onClick={() => navigator.clipboard?.writeText(path).catch(() => {})}
                  style={{ fontSize: 11.5, padding: "2px 8px", border: "1px solid var(--cs-border)", borderRadius: 5, background: "var(--surface)", color: "var(--text2)", cursor: "pointer", whiteSpace: "nowrap" }}>
                  Copy path
                </button>
              )}
              <a href={loc.url} target="_blank" rel="noreferrer" style={{ fontWeight: 700, color: "var(--cs-blue)", textDecoration: "none", whiteSpace: "nowrap" }}>Open folder ↗</a>
              {canChangeFolder && (
                <button type="button" title="Change the folder this turnover uploads to" aria-label="Change folder" onClick={() => setChangingFolder(true)}
                  style={{ fontSize: 13, padding: "1px 7px", border: "1px solid var(--cs-border)", borderRadius: 5, background: "var(--surface)", cursor: "pointer" }}>
                  ✏️
                </button>
              )}
            </div>
            {pendingDocs.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4, padding: "6px 10px", background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 6, fontSize: 12 }}>
                <span style={{ flex: 1, color: "#92400e" }}>
                  ⚠ {pendingDocs.length} file{pendingDocs.length === 1 ? " is" : "s are"} still in the Estimating site (uploaded before the job number), not in the job folder.
                  {copyingDocs && <span style={{ marginLeft: 8, color: "var(--text3)" }}>{copyingDocs}</span>}
                </span>
                {canChangeFolder && (
                  <button type="button" disabled={!!copyingDocs || !graphToken || !driveInfo} onClick={copyPendingDocs}
                    style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", border: "none", borderRadius: 5, background: "#92400e", color: "#fff", cursor: "pointer", whiteSpace: "nowrap" }}>
                    Copy into the job folder
                  </button>
                )}
              </div>
            )}
            </>
          );
        })()}

        {changingFolder && (
          <ChangeFolderDialog
            turnover={turnover}
            graphToken={graphToken}
            driveInfo={driveInfo}
            sectionFolderNames={subfolderNames(layoutOf(turnover))}
            onClose={() => setChangingFolder(false)}
            onSaved={(fields, created) => {
              setTurnover(t => (t ? { ...t, ...fields } : t));
              setChangingFolder(false);
              if (created.length) alert(`Folder changed. Created in it: ${created.join(", ")}.`);
            }}
          />
        )}

        {/* Progress bar — segmented by lifecycle stage, with next-step caption */}
        <div style={{ marginTop: 10 }}>
          {(() => {
            const stageIdx = TURNOVER_STAGES.indexOf(turnover.Status);
            return (
              <div style={{ display: "flex", gap: 4 }}>
                {TURNOVER_STAGES.map((stage, i) => {
                  const isDone    = stageIdx >= 0 && i < stageIdx;
                  const isCurrent = i === stageIdx;
                  let bg = "#e5e7eb"; // upcoming
                  if (isDone)    bg = "#10b981";
                  if (isCurrent) bg = stage === "Package Complete" || stage === "Turned Over" ? "#10b981" : "#f59e0b";
                  return (
                    <div key={stage} style={{ flex: 1 }} title={stage}>
                      <div style={{ height: 6, background: bg, borderRadius: 99, transition: "background 0.3s" }} />
                    </div>
                  );
                })}
              </div>
            );
          })()}
          {/* ── Next Required Action — the single dominant "what happens next" ──
              Replaces the old scattered Next Action Banner / submit-for-job-number
              banner / pending-number warning, which each showed a different slice
              of the same underlying question. One owner, one action, one reason,
              one button — see getNextRequiredAction() above. */}
          <NextActionBanner
            next={getNextRequiredAction()}
            isMobile={isMobile}
            submittingJobNumber={submittingJobNumber}
            completePct={completePct}
            sections={sections}
            onPrint={generatePackagePdf}
            onSubmitJobNumber={handleSubmitJobNumber}
            goToSection={goToSection}
            sharePointFolderUrl={turnover.SharePointFolderUrl}
          />
        </div>

        {/* Section completion bar (within current stage) */}
        <div style={{ marginTop: 6 }}>
          <div style={{ height: 4, background: "#e5e7eb", borderRadius: 99, overflow: "hidden" }}>
            <div style={{
              width: `${completePct}%`, height: "100%", borderRadius: 99, transition: "width 0.4s",
              background: completePct === 100 ? "#10b981" : completePct > 60 ? "#3b82f6" : "#f59e0b",
            }} />
          </div>
        </div>

        {/* SP connection warning */}
        {spError && (
          <div style={{ marginTop: 8, padding: "6px 12px", background: "#fee2e2", borderRadius: 6, fontSize: 11, color: "#dc2626" }}>
            ⚠️ {spError}
          </div>
        )}
      </div>

      {/* ── Body: sidebar + main panel ───────────────────────────────────────── */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden", position: "relative" }}>

        <SectionSidebar
          isMobile={isMobile}
          sections={sections}
          activeSection={activeSection}
          getNextRequiredAction={getNextRequiredAction}
          getSectionHealth={getSectionHealth}
          openActions={openActions}
          selectSection={selectSection}
        />

        {/* Main panel */}
        <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", marginTop: isMobile ? 44 : 0 }}>
          {activeSection && (
            <div style={{ padding: isMobile ? "14px" : "24px 28px", flex: 1 }}>

              {/* ── SECTION 1: COVER SHEET ─────────────────────────────────── */}
              {activeSection.SectionNumber === 1 ? (
                <CoverSheetPanel
                  turnover={turnover}
                  optionRows={turnoverConfigOptions}
                  sections={sections}
                  activeSection={activeSection}
                  coverForm={coverForm}
                  setCover={setCover}
                  coverDirty={coverDirty}
                  savingCover={savingCover}
                  hsCompanies={hsCompanies}
                  hsContacts={hsContacts}
                  hsLineItems={hsLineItems}
                  hsLoading={hsLoading}
                  hsCacheTime={hsCacheTime}
                  pmList={pmList}
                  pmListLoading={pmListLoading}
                  directoryFailed={directoryFailed}
                  isAssignedOwner={isAssignedOwner}
                  getCoverSheetMissing={getCoverSheetMissing}
                  onToggleComplete={toggleCoverSheetComplete}
                  onRefreshHubSpot={() => {
                    setHsCompanies([]);
                    setHsContacts([]);
                    setHsLineItems([]);
                    loadHubSpotCompanies(turnover.HubSpotDealID);
                  }}
                  onSave={saveCoverSheet}
                  onCancel={() => { setCoverForm(f => ({ ...f })); setCoverDirty(false); }}
                  addCommissionRow={addCommissionRow}
                  setNoCommission={setNoCommission}
                  updateCommissionRow={updateCommissionRow}
                  removeCommissionRow={removeCommissionRow}
                  goToSection={goToSection}
                />
              ) : (
                /* ── ALL OTHER SECTIONS ────────────────────────────────────── */
                <>
                  {/* Section header */}
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
                    <div>
                      <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 3 }}>
                        Section {activeSection.SectionNumber} of {sections.length}
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: "var(--text)" }}>
                        {activeSection.SectionName}
                      </div>
                      {isConditionalSection(turnover, activeSection.SectionNumber) && (
                        <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>
                          Conditional section — mark N/A if not applicable to this project
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                      {activeSection.SectionNumber !== CONTRACT_REVIEW_MATRIX_SECTION && (
                        <AssigneeControl
                          section={activeSection}
                          directoryUsers={directoryUsers}
                          directoryFailed={directoryFailed}
                          onReassign={reassignSection}
                          onReset={resetSectionAssignment}
                        />
                      )}
                      <button
                        onClick={toggleNA}
                        disabled={!isAssignedOwner(activeSection)}
                        title={!isAssignedOwner(activeSection) ? `Only ${activeSection.AssignedTo || "the assigned owner"} can change this section's status` : ""}
                        style={{
                          padding: "5px 12px", fontSize: 12, fontWeight: 600,
                          border: "1px solid var(--cs-border)", borderRadius: 6,
                          cursor: !isAssignedOwner(activeSection) ? "not-allowed" : "pointer",
                          background: !activeSection.IsApplicable ? "#e5e7eb" : "transparent",
                          color: !activeSection.IsApplicable ? "var(--text2)" : "var(--text3)",
                          opacity: !isAssignedOwner(activeSection) ? 0.5 : 1,
                        }}>
                        {activeSection.IsApplicable ? "Mark N/A" : "Mark Applicable"}
                      </button>
                      {activeSection.IsApplicable && activeSection.SectionNumber === CONTRACT_REVIEW_MATRIX_SECTION ? (
                        (() => {
                          const allSignedOff = contractReview.length > 0 &&
                            contractReview.every(r => r.Status === "Approved" || r.Status === "Approved with Comments");
                          const hasRejection = contractReview.some(r => r.Status === "Rejected");
                          return (
                            <span style={{
                              padding: "5px 12px", fontSize: 12, fontWeight: 600,
                              border: "1px solid " + (hasRejection ? "#fca5a5" : allSignedOff ? "#10b981" : "var(--cs-border)"),
                              borderRadius: 6,
                              background: hasRejection ? "#fee2e2" : allSignedOff ? "#d1fae5" : "transparent",
                              color: hasRejection ? "#991b1b" : allSignedOff ? "#065f46" : "var(--text3)",
                            }}>
                              {hasRejection ? "⚠ Rejected" : allSignedOff ? "✓ Complete" : `${contractReview.filter(r => r.Status === "Pending").length} of ${contractReview.length} pending`}
                            </span>
                          );
                        })()
                      ) : activeSection.IsApplicable && (
                        <button
                          onClick={toggleComplete}
                          disabled={!isAssignedOwner(activeSection)}
                          title={!isAssignedOwner(activeSection) ? `Only ${activeSection.AssignedTo || "the assigned owner"} can mark this complete` : ""}
                          style={{
                            padding: "5px 12px", fontSize: 12, fontWeight: 600,
                            border: "1px solid " + (activeSection.IsComplete ? "#10b981" : "var(--cs-border)"),
                            borderRadius: 6,
                            cursor: !isAssignedOwner(activeSection) ? "not-allowed" : "pointer",
                            background: activeSection.IsComplete ? "#d1fae5" : "transparent",
                            color: activeSection.IsComplete ? "#065f46" : "var(--text3)",
                            opacity: !isAssignedOwner(activeSection) ? 0.5 : 1,
                          }}>
                          {activeSection.IsComplete ? "✓ Complete" : "Mark Complete"}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Cannot Complete panel — appears only after a blocked "Mark Complete"
                      click, listing exactly what's outstanding (see toggleComplete /
                      getSectionMissing above). Clears automatically on navigating away. */}
                  {completionBlockers && completionBlockers.sectionId === activeSection.SectionID && (
                    <div style={{ marginBottom: 20, padding: "12px 16px", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#991b1b", marginBottom: 6 }}>
                        Cannot Complete Section — {completionBlockers.missing.length} item{completionBlockers.missing.length !== 1 ? "s" : ""} remain
                      </div>
                      {completionBlockers.missing.map((m, i) => (
                        <div key={i} style={{ fontSize: 12, color: "#991b1b", display: "flex", alignItems: "center", gap: 6, marginTop: i > 0 ? 3 : 0 }}>
                          <span>□</span> {m}
                        </div>
                      ))}
                    </div>
                  )}

                  {!activeSection.IsApplicable ? (
                    <div style={{ padding: 24, background: "#f9fafb", border: "1px solid var(--cs-border)", borderRadius: 10, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>
                      This section is marked <strong>Not Applicable</strong> for this project.
                      <br />Click "Mark Applicable" to re-enable it.
                    </div>
                  ) : MULTI_DOC_SECTIONS.has(activeSection.SectionNumber) ? (
                    /* ── MULTI-DOCUMENT LOG (with optional special panels) ── */
                    <div>
                      {/* Generic per-section help/context callout — see SECTION_HELP above.
                          Sections with their own bespoke block (4, 6's estimate textarea,
                          14, 16) render this in addition, ahead of their specific panel. */}
                      {SECTION_HELP[activeSection.SectionNumber] && (() => {
                        const help = SECTION_HELP[activeSection.SectionNumber];
                        return (
                          <div style={{
                            marginBottom: 20, padding: "12px 16px",
                            background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10,
                            display: "flex", alignItems: "flex-start", gap: 10,
                          }}>
                            <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{help.icon}</span>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 4 }}>
                                {help.title}
                              </div>
                              <div style={{ fontSize: 12, color: "#334155", lineHeight: 1.5 }}>
                                {help.body}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Layout-1 §4 — Administrative Documents: Safety sub-segment callout.
                          Safety docs (site safety plan, OSHA, OCIP, owner programs) live here
                          but are called out as their own labeled group so they don't get buried.
                          Layout 2 lists them as rows of the Contract Requirements checklist. */}
                      {sectionKind(turnover, activeSection.SectionNumber) === "admin" && (
                        <div style={{
                          marginBottom: 20, padding: "12px 16px",
                          background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 10,
                          display: "flex", alignItems: "flex-start", gap: 10,
                        }}>
                          <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>🦺</span>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "#9a3412", marginBottom: 4 }}>
                              Safety & Insurance Documents
                            </div>
                            <div style={{ fontSize: 12, color: "#c2410c", lineHeight: 1.5 }}>
                              Upload site-specific safety plans, OSHA compliance docs, owner safety programs,
                              OCIP/CCIP enrollment forms, and insurance certificates here. Use the{" "}
                              <strong>Select type</strong> dropdown to tag each document — safety-specific
                              types appear at the bottom of the list.
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Section 3 — contract execution: the "contract signed" step,
                          gated on sign-offs + accepted contract issues. */}
                      {activeSection.SectionNumber === 3 && (
                        <ContractExecutionPanel
                          reviews={contractReview}
                          issues={contractIssues}
                          turnover={turnover}
                          documents={documents}
                          section3={activeSection}
                          canAct={canResolveIssues(turnover, activeSection, currentUser, isAdmin)}
                          busy={contractBusy}
                          onSetDocType={handleSetDocType}
                          onGoToReview={() => goToSection(CONTRACT_REVIEW_MATRIX_SECTION)}
                          isAdmin={isAdmin}
                          onRequestException={handleJobNumberException}
                          onExecute={(v) => runContractExecution(markExecutedPayload(parseInt(turnoverID), v, currentUser))}
                          onClear={() => { if (window.confirm("Clear the executed-contract record? Section 3 reopens.")) runContractExecution(clearExecutedPayload(parseInt(turnoverID))); }}
                        />
                      )}

                      {/* Section 4 — Financial Compliance: structured record ported
                          from the estimate at turnover creation, tracked here on. */}
                      {sectionKind(turnover, activeSection.SectionNumber) === "marketing" && (
                        <MarketingKickoffPanel
                          key={turnover.TurnoverID}
                          turnover={turnover}
                          schedule={schedule}
                          data={marketingKickoff}
                          onChange={setMarketingKickoff}
                          canEdit={isAssignedOwner(activeSection)}
                          user={currentUser}
                          optionRows={turnoverConfigOptions}
                          canScheduleMeeting={turnover.Status === "Package Complete" || turnover.Status === "Turned Over"}
                          onScheduleMeeting={generatePackagePdf}
                        />
                      )}
                      {sectionKind(turnover, activeSection.SectionNumber) === "requirements" && (
                        <ContractRequirementsPanel
                          rows={contractRequirements}
                          optionRows={turnoverConfigOptions}
                          sectionDocs={documents.filter(d => d.SectionID === activeSection.SectionID)}
                          financialCompliance={financialCompliance}
                          canEdit={isAssignedOwner(activeSection)}
                          saving={savingRequirement}
                          onSave={saveContractRequirement}
                        />
                      )}
                      {activeSection.SectionNumber === FINANCIAL_COMPLIANCE_SECTION && (
                        <FinancialCompliancePanel
                          compliance={financialCompliance}
                          onSave={saveFinancialCompliance}
                          saving={savingFinancialCompliance}
                          hiddenKeys={sectionNumberOf(turnover, "requirements") ? REQUIREMENT_COVERED_FC_KEYS : undefined}
                        />
                      )}

                      {/* Section 6: Estimate summary text field */}
                      {activeSection.SectionNumber === ESTIMATE_SUMMARY_SECTION && (
                        <ReleaseReconciliation
                          lineItems={hsLineItems}
                          sectionDocs={documents.filter(d => d.SectionID === activeSection.SectionID)}
                          cacheTime={hsCacheTime}
                          onOpenCoverSheet={() => goToSection(1)}
                        />
                      )}
                      {activeSection.SectionNumber === ESTIMATE_SUMMARY_SECTION && (
                        <div style={{ marginBottom: 20 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text2)", marginBottom: 8 }}>
                            Estimate Summary / Key Numbers
                          </div>
                          <textarea
                            value={notesText}
                            onChange={e => { setNotesText(e.target.value); setNotesDirty(true); }}
                            onBlur={saveNotes}
                            placeholder="Enter key estimate figures — total cost, margin %, contingency, major cost categories, etc."
                            rows={4}
                            style={{
                              width: "100%", padding: "10px 12px", fontSize: 13,
                              border: "1px solid " + (notesDirty ? "var(--cs-blue)" : "var(--cs-border)"),
                              borderRadius: 8, resize: "vertical", boxSizing: "border-box",
                              background: "var(--surface)", color: "var(--text)",
                              fontFamily: "inherit", lineHeight: 1.5, outline: "none",
                            }}
                          />
                        </div>
                      )}

                      {/* Section 14 — Correspondence: per-document analyze + timeline
                          view replaces the old bulk "Summarize with AI" banner. The
                          Analyze/Summary buttons live on each document row (same
                          mechanism as Contract Review) and "View Timeline" appears
                          in the Documents header once any doc has been analyzed. */}
                      {activeSection.SectionNumber === CORRESPONDENCE_SECTION && (() => {
                        const corrSection = sections.find(s => s.SectionNumber === CORRESPONDENCE_SECTION);
                        const combinedText = corrSection
                          ? aiSummaries
                              .filter(s => s.SummaryType?.startsWith('correspondence_combined_s') && s.SectionID === corrSection.SectionID)
                              .sort(bySummaryNum)
                              .map(s => s.SummaryText)
                              .join('\n\n')
                          : '';
                        const isGenerating = analyzingDoc === 'combined';
                        return (
                          <div style={{ marginBottom: 20 }}>
                            {combinedText ? (
                              <div style={{
                                padding: "14px 16px", background: "var(--surface)",
                                border: "1px solid var(--cs-border)", borderRadius: 8,
                                borderLeft: "3px solid var(--cs-blue)",
                              }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--cs-blue)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                  AI Correspondence Summary {isGenerating && <span style={{ fontWeight: 400, textTransform: "none" }}>— updating…</span>}
                                </div>
                                <div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                                  {combinedText}
                                </div>
                              </div>
                            ) : (
                              <div style={{
                                padding: "14px 16px", background: "#f0f9ff",
                                border: "1px solid #bae6fd", borderRadius: 10,
                              }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: "#0369a1", marginBottom: 4 }}>
                                  {isGenerating ? '⏳ Generating summary…' : '🕐 AI Correspondence Review'}
                                </div>
                                <div style={{ fontSize: 12, color: "#1e3a5f" }}>
                                  Upload emails, meeting notes, and other correspondence below. Claude analyzes each document automatically and builds a rolling summary — scope clarifications, client commitments, exclusions, and open items — that updates each time a new document is added.
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Section 16 — Permits, Bonds & AHJ Requirements: structured terms */}
                      {sectionKind(turnover, activeSection.SectionNumber) === "permits" && (
                        <PermitsTermsPanel
                          permitsTerms={permitsTerms}
                          setPermitsTerms={setPermitsTerms}
                          onSave={savePermitsTerms}
                          saving={savingPermits}
                        />
                      )}

                      {activeSection.SectionNumber === SCHEDULE_SECTION && (
                        <ScheduleMilestonesPanel
                          schedule={schedule}
                          savingId={savingMilestone}
                          newMilestone={newMilestone}
                          adding={addingMilestone}
                          onSave={saveMilestone}
                          onDelete={deleteMilestone}
                          onNewMilestoneChange={setNewMilestone}
                          onAdd={addCustomMilestone}
                          extracting={scheduleJobStatus === "Processing"}
                          scheduleJobFailed={scheduleJobStatus === "Failed"}
                          proposedMilestones={proposedMilestones}
                          onExtract={extractScheduleMilestones}
                          onProposedChange={setProposedMilestones}
                          onDiscardProposed={discardProposedMilestones}
                          onAcceptProposed={acceptProposedMilestones}
                          accepting={acceptingProposed}
                          scheduleSummary={aiSummaries.find(s => s.SummaryType === 'schedule_summary')?.SummaryText || null}
                        />
                      )}

                      {/* Section 8 — Long Lead Items: structured tracker living alongside
                          the vendor quote document log, same inline-edit pattern as the
                          Section 9 schedule table above. */}
                      {activeSection.SectionNumber === LONG_LEAD_SECTION && (
                        <LongLeadItemsPanel
                          items={longLeadItems}
                          savingId={savingLongLeadItem}
                          newItem={newLongLeadItem}
                          adding={addingLongLeadItem}
                          onSave={saveLongLeadItem}
                          onDelete={deleteLongLeadItem}
                          onNewItemChange={setNewLongLeadItem}
                          onAdd={addCustomLongLeadItem}
                        />
                      )}

                      {/* Section 6 — RACI Matrix: structured responsibility grid
                          living alongside the section's document log. CS-responsible
                          rows push to the estimator as a scope action item. */}
                      {activeSection.SectionNumber === RACI_SECTION && (
                        <RaciMatrixPanel
                          items={raciItems}
                          parties={raciParties}
                          assignments={raciAssigns}
                          savingId={savingRaciItem}
                          onCycleMark={cycleRaciMark}
                          onSaveItem={saveRaciItem}
                          onDeleteItem={deleteRaciItem}
                          newItem={newRaciItem}
                          onNewItemChange={setNewRaciItem}
                          onAdd={addCustomRaciItem}
                          adding={addingRaciItem}
                          onSeed={seedRaciItems}
                          seeding={seedingRaci}
                          onRenameParty={renameRaciParty}
                          onAddParty={addRaciParty}
                          onDeleteParty={deleteRaciParty}
                          addingParty={addingRaciParty}
                          onSendScope={null /* RACI still in development — send-scope email hidden (_sendRaciScopeToEstimator) */}
                          sendingScope={sendingRaciScope}
                          estimatorName={turnover?.Estimator || null}
                        />
                      )}

                    <MultiDocPanel
                      key={activeSection.SectionID}
                      section={activeSection}
                      documents={documents.filter(d => d.SectionID === activeSection.SectionID)}
                      docTypes={withExtraDocTypes(sectionKind(turnover, activeSection.SectionNumber), docTypesFor(turnover, activeSection.SectionNumber), turnoverConfigOptions)}
                      requireValueForDocType={activeSection.SectionNumber === ESTIMATE_SUMMARY_SECTION ? ESTIMATE_DOC_TYPE : null}
                      groups={activeSection.SectionNumber === ESTIMATE_SUMMARY_SECTION
                        ? estimateDocGroups(hsLineItems, documents.filter(d => d.SectionID === activeSection.SectionID))
                        : null}
                      canUpload={canUpload}
                      uploadLockMessage={uploadLockMessage}
                      addDocForm={addDocForm}
                      setAddDocForm={setAddDocForm}
                      showAddDoc={showAddDoc}
                      setShowAddDoc={setShowAddDoc}
                      docUploading={docUploading}
                      onUpload={handleDocUpload}
                      onDelete={handleDeleteDoc}
                      onChangeType={handleSetDocType}
                      lockedDocIds={turnover?.ContractExecutedDocumentID ? [turnover.ContractExecutedDocumentID] : []}
                      onAnalyzeDoc={
                        activeSection.SectionNumber === AI_CONTRACT_REVIEW_SECTION ? (doc) => runContractAnalyzeOnly(doc)
                        : activeSection.SectionNumber === CORRESPONDENCE_SECTION ? (doc) => analyzeDocViaBackend(doc)
                        : null
                      }
                      analyzingDoc={analyzingDoc}
                      contractJobStatus={
                        activeSection.SectionNumber === AI_CONTRACT_REVIEW_SECTION ? contractJobStatus
                        : activeSection.SectionNumber === CORRESPONDENCE_SECTION ? corrJobStatus
                        : null
                      }
                      docAiSummaries={aiSummaries.filter(s => s.SectionID === activeSection.SectionID)}
                      onShowDocSummary={(docId) => setShowSummaryFlyout(docId)}
                      onShowCombinedSummary={() => setShowSummaryFlyout(
                        `combined_${activeSection.SectionNumber === CORRESPONDENCE_SECTION ? CORRESPONDENCE_PROMPT_CATEGORY : "contract_review"}`
                      )}
                      hasCombinedSummary={aiSummaries.some(s => s.SectionID === activeSection.SectionID && s.SummaryType?.startsWith(
                        `${activeSection.SectionNumber === CORRESPONDENCE_SECTION ? CORRESPONDENCE_PROMPT_CATEGORY : "contract_review"}_combined`
                      ))}
                      onRunCombined={
                        activeSection.SectionNumber === AI_CONTRACT_REVIEW_SECTION ? () => runCombinedSummary(AI_CONTRACT_REVIEW_SECTION, "contract_review", "contract_review")
                        : activeSection.SectionNumber === CORRESPONDENCE_SECTION ? () => runCombinedSummary(CORRESPONDENCE_SECTION, CORRESPONDENCE_PROMPT_CATEGORY, CORRESPONDENCE_PROMPT_CATEGORY)
                        : null
                      }
                      notesText={notesText}
                      setNotesText={setNotesText}
                      notesDirty={notesDirty}
                      setNotesDirty={setNotesDirty}
                      saveNotes={saveNotes}
                      savingNotes={savingNotes}
                      enableQuoteScrub={activeSection.SectionNumber === VENDOR_QUOTE_SECTION}
                      aiPrefixBase={activeSection.SectionNumber === CORRESPONDENCE_SECTION ? CORRESPONDENCE_PROMPT_CATEGORY : "contract_review"}
                      turnover={turnover}
                      currentUser={currentUser}
                    />
                      {/* Per-doc AI analysis handled inline on document rows */}

                      {/* Section 2: Contract Review Matrix — responsibilities sign-off */}
                      {activeSection.SectionNumber === CONTRACT_REVIEW_MATRIX_SECTION && contractReview.length > 0 && (
                        <ContractReviewPanel
                          rows={contractReview}
                          onSave={saveContractReview}
                          onUpdate={updateReviewRow}
                          savingId={savingReview}
                          currentUser={currentUser}
                          onSendReview={sendContractReview}
                          sending={sendingReview}
                          directoryUsers={directoryUsers}
                          checklist={hasReviewChecklist(turnover) ? {
                            items: contractReviewItems,
                            optionRows: turnoverConfigOptions,
                            canAnswer: (row) => canActForReview(row, currentUser, isAdmin) && !turnover.ContractExecutedDate,
                            onAnswer: saveReviewItem,
                            onLogIssue: contractIssueHandlers.create,
                            busy: contractBusy,
                          } : null}
                        />
                      )}
                      {activeSection.SectionNumber === CONTRACT_REVIEW_MATRIX_SECTION && contractReview.length > 0 && (
                        <ContractIssuesPanel
                          reviews={contractReview}
                          issues={contractIssues}
                          turnover={turnover}
                          section3={sections.find(s => s.SectionNumber === 3)}
                          documents={documents}
                          currentUser={currentUser}
                          isAdmin={isAdmin}
                          busy={contractBusy}
                          handlers={contractIssueHandlers}
                        />
                      )}

                      {/* Section navigation — always last, after all panels. Next is a
                          filled primary button naming the upcoming section so users
                          always know what comes next in the flow; Prev stays right
                          next to it. */}
                      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--cs-border)" }}>
                        {activeSection.SectionNumber > 1 && (
                          <button onClick={() => goToSection(activeSection.SectionNumber - 1)} style={{ padding: "8px 18px", fontSize: 13, fontWeight: 600, background: "none", border: "1.5px solid var(--cs-border)", borderRadius: 6, cursor: "pointer", color: "var(--text)" }}>← Prev</button>
                        )}
                        {activeSection.SectionNumber < lastSectionNumber(turnover) && (() => {
                          const nextSec = sections.find(s => s.SectionNumber === activeSection.SectionNumber + 1);
                          return (
                            <button onClick={() => goToSection(activeSection.SectionNumber + 1)} style={{ padding: "9px 20px", fontSize: 13, fontWeight: 700, background: NAVY_ACTION, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", boxShadow: "0 1px 3px rgba(30,58,95,0.35)" }}>
                              {nextSec ? `Next: ${nextSec.SectionName} →` : "Next →"}
                            </button>
                          );
                        })()}
                      </div>
                    </div>
                  ) : (
                    /* ── STANDARD SINGLE UPLOAD ───────────────────────────── */
                    <>
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text2)", marginBottom: 8 }}>Document Upload</div>
                        {!canUpload ? (
                          <div style={{ padding: 16, background: "#fffbeb", border: "1px solid #f59e0b", borderRadius: 8, fontSize: 12, color: "#92400e" }}>
                            {uploadLockMessage || "🔒 Uploads unlock once Accounting assigns a job number."}
                          </div>
                        ) : (
                          <>
                            <DropZone onFile={handleFileUpload} uploading={uploading} />
                            {activeSection.SharePointUrl && (
                              <div style={{ marginTop: 10, padding: "10px 14px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, display: "flex", alignItems: "center", gap: 10 }}>
                                <span style={{ fontSize: 16 }}>📄</span>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 13, fontWeight: 600, color: "#065f46" }}>{activeSection.FileName}</div>
                                  <div style={{ fontSize: 11, color: "#059669", marginTop: 1 }}>Uploaded {fmtDateTime(activeSection.UploadedAt)} by {activeSection.UploadedBy}</div>
                                </div>
                                <a href={activeSection.SharePointUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "var(--cs-blue)", fontWeight: 600, textDecoration: "none" }}>View ↗</a>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <div>
                        {sectionKind(turnover, activeSection.SectionNumber) !== "permits" && (
                          <>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text2)" }}>Notes</div>
                              {notesDirty && (
                                <button onClick={saveNotes} disabled={savingNotes} style={{ padding: "4px 12px", fontSize: 12, fontWeight: 600, background: "var(--cs-blue)", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer", opacity: savingNotes ? 0.5 : 1 }}>
                                  {savingNotes ? "Saving…" : "Save Notes"}
                                </button>
                              )}
                            </div>
                            <textarea value={notesText} onChange={e => { setNotesText(e.target.value); setNotesDirty(true); }} onBlur={saveNotes} placeholder="Add notes for this section…" rows={5}
                              style={{ width: "100%", padding: "10px 12px", fontSize: 13, border: "1px solid " + (notesDirty ? "var(--cs-blue)" : "var(--cs-border)"), borderRadius: 8, resize: "vertical", boxSizing: "border-box", background: "var(--surface)", color: "var(--text)", fontFamily: "inherit", lineHeight: 1.5, outline: "none" }} />
                          </>
                        )}
                      </div>
                      {/* Prev/Next: filled primary "Next" names the upcoming section so
                          users always know what comes next in the flow. */}
                      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginTop: 24, paddingTop: 16, borderTop: "1px solid var(--cs-border)" }}>
                        {activeSection.SectionNumber > 1 && (
                          <button onClick={() => goToSection(activeSection.SectionNumber - 1)} style={{ padding: "8px 18px", fontSize: 13, fontWeight: 600, background: "none", border: "1.5px solid var(--cs-border)", borderRadius: 6, cursor: "pointer", color: "var(--text)" }}>← Prev</button>
                        )}
                        {activeSection.SectionNumber < lastSectionNumber(turnover) && (() => {
                          const nextSec = sections.find(s => s.SectionNumber === activeSection.SectionNumber + 1);
                          return (
                            <button onClick={() => goToSection(activeSection.SectionNumber + 1)} style={{ padding: "9px 20px", fontSize: 13, fontWeight: 700, background: NAVY_ACTION, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", boxShadow: "0 1px 3px rgba(30,58,95,0.35)" }}>
                              {nextSec ? `Next: ${nextSec.SectionName} →` : "Next →"}
                            </button>
                          );
                        })()}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Action Items Panel ──────────────────────────────────────────── */}
          <div style={{
            borderTop: "1px solid var(--cs-border)",
            padding: "20px 28px",
            background: "#fafbfc",
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14, color: "var(--text)" }}>
              Action Items
              {openActions.length > 0 && (
                <span style={{
                  marginLeft: 8, fontSize: 11, fontWeight: 700,
                  background: "#fee2e2", color: "#dc2626",
                  padding: "2px 7px", borderRadius: 100,
                }}>
                  {openActions.length} open
                </span>
              )}
            </div>

            {/* Existing action items — open only */}
            {openActions.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                {openActions.map(item => (
                  <ActionItemRow
                    key={item.ActionItemID}
                    item={item}
                    turnoverID={parseInt(turnoverID)}
                    onUpdated={load}
                    currentUser={currentUser}
                  />
                ))}
              </div>
            )}

            {/* Add new action item */}
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <div style={{ flex: 2 }}>
                <input
                  value={newAction.description}
                  onChange={e => setNewAction(a => ({ ...a, description: e.target.value }))}
                  onKeyDown={e => e.key === "Enter" && addActionItem()}
                  placeholder="Add action item…"
                  style={{
                    width: "100%", padding: "7px 10px", fontSize: 13,
                    border: "1px solid var(--cs-border)", borderRadius: 6,
                    background: "var(--surface)", color: "var(--text)", boxSizing: "border-box",
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <select
                  value={newAction.assigned_to}
                  onChange={e => {
                    const picked = directoryUsers.find(u => u.label === e.target.value);
                    setNewAction(a => ({
                      ...a,
                      assigned_to:    picked?.label || e.target.value,
                      assigned_email: picked?.email || "",
                    }));
                  }}
                  style={{
                    width: "100%", padding: "7px 10px", fontSize: 13,
                    border: "1px solid var(--cs-border)", borderRadius: 6,
                    background: "var(--surface)", color: newAction.assigned_to ? "var(--text)" : "var(--text3)",
                    boxSizing: "border-box", cursor: "pointer",
                  }}
                >
                  <option value="">Assign to…</option>
                  {directoryUsers.map(u => (
                    <option key={u.email} value={u.label}>{u.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <input
                  type="date"
                  value={newAction.due_date}
                  onChange={e => setNewAction(a => ({ ...a, due_date: e.target.value }))}
                  style={{
                    padding: "7px 10px", fontSize: 13,
                    border: "1px solid var(--cs-border)", borderRadius: 6,
                    background: "var(--surface)", color: "var(--text)",
                  }}
                />
              </div>
              <button
                onClick={addActionItem}
                disabled={addingAction || !newAction.description.trim()}
                style={{
                  padding: "7px 16px", fontSize: 13, fontWeight: 600,
                  background: "var(--cs-blue)", color: "#fff",
                  border: "none", borderRadius: 6, cursor: "pointer",
                  opacity: (addingAction || !newAction.description.trim()) ? 0.5 : 1,
                  whiteSpace: "nowrap",
                }}
              >
                {addingAction ? "Adding…" : "Add"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
